import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { useFloating, offset, flip, shift } from '@floating-ui/react';
import { useAppStore } from '../../../store/appStore';
import { MatterStatus, BillingMethod, CreateMatterDto, MatterDetailed, OpposingAttorney, RelatedProceeding, ProceedingRelationshipType, RelatedProceedingStatus, StrategicImportance } from '../../../types/matters';
import { useContacts } from '../../contacts/hooks/useContacts';
import { useMatters } from '../hooks/useMatters';
import { useAttorneys } from '../hooks/useAttorneys';
import LexicalEditor from '../../../components/editors/LexicalEditor';
import { MatterTemplate } from '../../../services/matterTemplateService';
import { useMatterTemplates } from '../hooks/useMatterTemplates';
import CourtIntelligencePreview from './CourtIntelligencePreview';
import { courtIntelligenceApi } from '../api/courtIntelligenceApi';
import SubtypePicker from './SubtypePicker';
import TaskTemplatePreviewPanel from './TaskTemplatePreviewPanel';
import TaskTemplateComparisonModal from './TaskTemplateComparisonModal';
import { fetchMailboxAccounts } from '../../edgemail-client/services/MailClientAPI';
import type { MailAccount } from '../../edgemail-client/types/mail.types';
import {
  MattersIcon,
  ContactsIcon, 
  DocumentsIcon,
  CalendarIcon, 
  BillingIcon,
  SettingsIcon,
  UserCheckIcon,
  EmailIcon
} from '../../../components/icons';

// Enhanced interfaces based on NewMatterForm.md specification
interface TemplateSection {
  useTemplate: boolean;
  selectedTemplateId?: string;
  availableTemplates: MatterTemplate[];
}

interface MatterDetailsSection {
  clientId: string;
  description: string;
  responsibleAttorneyId?: string;
  originatingAttorneyId?: string;
  responsibleStaffId?: string;
  clientReferenceNumber?: string;
  stateCode?: string;
  practiceAreaId?: string;
  jurisdictionCode?: string;
  caseSubtype?: string;
  plaintiffPetitioner?: string;
  defendantRespondent?: string;
  opposingAttorneys: OpposingAttorney[];
  stageId?: string;
  status: MatterStatus;
}

interface PermissionsSection {
  accessLevel: 'everyone' | 'specific';
  allowedUserIds: string[];
  allowedGroupIds: string[];
}

interface NotificationsSection {
  notificationUserIds: string[];
}

interface BlockUsersSection {
  blockedUserIds: string[];
}

interface RelatedContact {
  contactId: string;
  relationship: string;
  isBillRecipient: boolean;
}

interface RelatedContactsSection {
  relatedContacts: RelatedContact[];
}

interface CustomField {
  id: string;
  name: string;
  type: 'text' | 'number' | 'date' | 'dropdown' | 'checkbox';
  value: string | number | boolean | Date;
  required: boolean;
}

interface CustomFieldsSection {
  fieldSetIds: string[];
  customFields: CustomField[];
}

interface CustomBillingRate {
  userId: string;
  rate: number;
  description?: string;
}

interface BillingSection {
  isBillable: boolean;
  billingMethod: BillingMethod;
  customRates: CustomBillingRate[];
  budget?: number;
  budgetAlertThresholds: Array<{ percentage: number; notify_users: string[] }>;
  enableSplitBilling: boolean;
  splitAllocationRules: Array<{ client_id: string; client_name: string; percentage: number; billing_contact_email?: string }>;
  retainerConfig: {
    enabled: boolean;
    minimum_threshold: number;
    replenish_amount: number;
    auto_replenish: boolean;
    notify_on_low_balance: boolean;
  };
}

interface TaskListsSection {
  taskListIds: string[];
  notifyAssignees: boolean;
  dueDateOffsets?: { [taskListId: string]: number };
}

interface DocumentFolder {
  name: string;
  categoryId: string;
}

interface MatterEmailAutomationParticipant {
  name?: string | null;
  email: string;
  role?: string | null;
}

interface MatterEmailAutomationSection {
  enabled: boolean;
  mailboxId: string;
  participants: MatterEmailAutomationParticipant[];
  includeExisting: boolean;
  createFutureRule: boolean;
}

interface DocumentFoldersSection {
  folders: DocumentFolder[];
  emailAutomation: MatterEmailAutomationSection;
}

interface MatterEmailAutomationPreview {
  enabled: boolean;
  mailboxId: string | null;
  targetFolderPath: string | null;
  participantEmails: string[];
  existingMatchCount: number;
  outsideTargetCount: number;
  samples: Array<{
    messageId: string;
    from: string | null;
    subject: string | null;
    folderPath: string | null;
    receivedAt: string | null;
  }>;
  binding?: {
    isEnabled: boolean;
    backfillStatus: string;
    lastMovedCount: number;
    targetFolderPath: string | null;
  } | null;
}

interface ReportsSection {
  originatingAttorneyAllocation: number;
  useFirmSettingsOriginating: boolean;
  responsibleAttorneyAllocation: number;
  useFirmSettingsResponsible: boolean;
}

interface RelatedProceedingDraft {
  proceeding_id?: string;
  relationship_type: ProceedingRelationshipType;
  title: string;
  style_of_case?: string;
  practice_area?: string;
  jurisdiction_code: string;
  court_name?: string;
  case_number?: string;
  filing_date?: string;
  status: RelatedProceedingStatus;
  role?: string;
  summary?: string;
  strategic_importance: StrategicImportance;
  affects_deadlines: boolean;
  affects_conflicts: boolean;
}

interface RelatedProceedingsSection {
  relatedProceedings: RelatedProceedingDraft[];
}

interface CaseNumberEntry {
  caseNumber: string;
  court: string;
  jurisdiction: string;
  jurisdictionCode: string;
  caseType: string;
  filedDate?: string;
  status: string;
}

/**
 * Extract the deterministic petition_filed_date from case numbers.
 * Priority: first entry with caseType 'Primary', else first entry with a non-empty filedDate.
 * Returns null (not undefined) when no filed date exists, enabling trigger-clear semantics.
 */
function extractPetitionFiledDate(caseNumbers: CaseNumberEntry[]): string | null {
  if (!caseNumbers || caseNumbers.length === 0) return null;
  const primary = caseNumbers.find(
    cn => cn.caseType?.toLowerCase() === 'primary' && cn.filedDate
  );
  if (primary?.filedDate) return primary.filedDate;
  const firstWithDate = caseNumbers.find(cn => cn.filedDate);
  return firstWithDate?.filedDate || null;
}

function defaultRelatedProceedingDraft(jurisdictionCode?: string, practiceArea?: string): RelatedProceedingDraft {
  return {
    relationship_type: 'companion_case',
    title: '',
    style_of_case: '',
    practice_area: practiceArea || '',
    jurisdiction_code: jurisdictionCode || '',
    court_name: '',
    case_number: '',
    filing_date: '',
    status: 'planned',
    role: '',
    summary: '',
    strategic_importance: 'medium',
    affects_deadlines: false,
    affects_conflicts: false,
  };
}

interface MatterCreateFormData {
  template: TemplateSection;
  details: MatterDetailsSection;
  caseNumbers: CaseNumberEntry[];
  relatedProceedings: RelatedProceedingsSection;
  permissions: PermissionsSection;
  notifications: NotificationsSection;
  blockUsers: BlockUsersSection;
  relatedContacts: RelatedContactsSection;
  customFields: CustomFieldsSection;
  billing: BillingSection;
  taskLists: TaskListsSection;
  documentFolders: DocumentFoldersSection;
  reports: ReportsSection;
}

interface NoticeModalState {
  title: string;
  message: string;
  tone?: 'info' | 'error' | 'success';
}

// Section navigation configuration
const FORM_SECTIONS = [
  { id: 'template', name: 'Template information', icon: SettingsIcon, color: 'text-purple-600' },
  { id: 'details', name: 'Engagement details', icon: MattersIcon, color: 'text-blue-600' },
  { id: 'relatedProceedings', name: 'Related proceedings', icon: MattersIcon, color: 'text-cyan-600' },
  { id: 'taskLists', name: 'Task lists', icon: CalendarIcon, color: 'text-orange-600' },
  { id: 'permissions', name: 'Engagement permissions', icon: UserCheckIcon, color: 'text-green-600' },
  { id: 'notifications', name: 'Engagement notifications', icon: EmailIcon, color: 'text-yellow-600' },
  { id: 'blockUsers', name: 'Block users', icon: UserCheckIcon, color: 'text-red-600' },
  { id: 'relatedContacts', name: 'Related contacts', icon: ContactsIcon, color: 'text-indigo-600' },
  { id: 'customFields', name: 'Custom fields', icon: SettingsIcon, color: 'text-gray-600' },
  { id: 'billing', name: 'Billing preference', icon: BillingIcon, color: 'text-emerald-600' },
  { id: 'documentFolders', name: 'Document folders', icon: DocumentsIcon, color: 'text-teal-600' },
  { id: 'reports', name: 'Reports', icon: BillingIcon, color: 'text-pink-600' }
];

const defaultMatterEmailAutomation = (): MatterEmailAutomationSection => ({
  enabled: false,
  mailboxId: '',
  participants: [],
  includeExisting: true,
  createFutureRule: true,
});

const messageFromUnknownError = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message.trim()) return error.message;
  if (typeof error === 'string' && error.trim()) return error;
  return fallback;
};

const readApiErrorMessage = async (response: Response, fallback: string): Promise<string> => {
  const text = await response.text();
  if (!text.trim()) return fallback;
  try {
    const parsed = JSON.parse(text);
    if (typeof parsed?.message === 'string' && parsed.message.trim()) return parsed.message;
    if (typeof parsed?.error === 'string' && parsed.error.trim()) return parsed.error;
  } catch {
    // Fall through to the raw response text.
  }
  return text;
};

/**
 * MatterCreateForm - Advanced multi-section matter creation with AI assistance
 * 
 * EdgeLex Standout Features:
 * 1. AI-Powered Smart Suggestions during form completion
 * 2. Real-time conflict checking as you type
 * 3. Intelligent template matching based on matter description
 * 4. Progressive form validation with visual feedback
 * 5. Auto-save with visual indicators
 * 6. Advanced rich text editor with legal templates
 * 7. Interactive section progress tracking
 * 8. Context-aware field suggestions
 * 
 * Enhanced Edit Mode Features:
 * 9. Change tracking and diff comparison
 * 10. Edit mode detection and proper update handling
 * 11. User feedback modal showing what was changed
 */
/** Inline info tooltip using floating-ui for contextual help. */
function InfoTooltip({ content }: { content: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const { refs, floatingStyles } = useFloating({
    open: isOpen,
    placement: 'top',
    middleware: [offset(8), flip(), shift({ padding: 8 })],
  });
  return (
    <>
      <button
        ref={refs.setReference}
        type="button"
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
        className="inline-flex ml-1 text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
        aria-label="More information"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
          <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
        </svg>
      </button>
      {isOpen && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          className="z-50 max-w-xs px-3 py-2 text-xs text-white bg-gray-900 dark:bg-gray-700 rounded-lg shadow-lg"
          role="tooltip"
        >
          {content}
        </div>
      )}
    </>
  );
}

const MatterCreateForm: React.FC = () => {
  const navigate = useNavigate();
  const { matterId } = useParams<{ matterId: string }>();
  const { contacts } = useAppStore();
  const { fetchContacts } = useContacts();
  const { keycloak } = useKeycloak();
  const { createMatter, updateMatter, fetchMatterById, getCurrentRealm, validateRealmContext } = useMatters();
  const { attorneys: realAttorneys } = useAttorneys();
  const { getMatterTemplates } = useMatterTemplates();

  // Edit Mode Detection
  const isEditMode = Boolean(matterId);

  // State management
  const [currentSection, setCurrentSection] = useState('template');
  const [existingMatter, setExistingMatter] = useState<MatterDetailed | null>(null);
  const [originalData, setOriginalData] = useState<MatterCreateFormData | null>(null);
  const [isLoadingMatter, setIsLoadingMatter] = useState(isEditMode);
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [changesSummary, setChangesSummary] = useState<Array<{field: string, oldValue: any, newValue: any}>>([]);
  const [pendingUpdates, setPendingUpdates] = useState<Partial<MatterDetailed> | null>(null);
  const [noticeModal, setNoticeModal] = useState<NoticeModalState | null>(null);

  const [formData, setFormData] = useState<MatterCreateFormData>({
    template: {
      useTemplate: false,
      selectedTemplateId: undefined,
      availableTemplates: []
    },
    details: {
      clientId: '',
      description: '',
      responsibleAttorneyId: undefined,
      originatingAttorneyId: undefined,
      responsibleStaffId: undefined,
      clientReferenceNumber: undefined,
      stateCode: undefined,
      practiceAreaId: undefined,
      jurisdictionCode: undefined,
      caseSubtype: undefined,
      plaintiffPetitioner: undefined,
      defendantRespondent: undefined,
      opposingAttorneys: [],
      stageId: undefined,
      status: 'active',
    },
    caseNumbers: [],
    relatedProceedings: {
      relatedProceedings: []
    },
    permissions: {
      accessLevel: 'everyone',
      allowedUserIds: [],
      allowedGroupIds: []
    },
    notifications: {
      notificationUserIds: []
    },
    blockUsers: {
      blockedUserIds: []
    },
    relatedContacts: {
      relatedContacts: []
    },
    customFields: {
      fieldSetIds: [],
      customFields: []
    },
    billing: {
      isBillable: true,
      billingMethod: 'Hourly',
      customRates: [],
      budget: undefined,
      budgetAlertThresholds: [],
      enableSplitBilling: false,
      splitAllocationRules: [],
      retainerConfig: {
        enabled: false,
        minimum_threshold: 0,
        replenish_amount: 0,
        auto_replenish: false,
        notify_on_low_balance: true,
      }
    },
    taskLists: {
      taskListIds: [],
      notifyAssignees: true,
      dueDateOffsets: {}
    },
    documentFolders: {
      folders: [],
      emailAutomation: defaultMatterEmailAutomation(),
    },
    reports: {
      originatingAttorneyAllocation: 0,
      useFirmSettingsOriginating: true,
      responsibleAttorneyAllocation: 0,
      useFirmSettingsResponsible: true
    }
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({});
  const [completedSections, setCompletedSections] = useState<Set<string>>(new Set());
  const [isDirty, setIsDirty] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<string[]>([]);
  const [conflictCheck, setConflictCheck] = useState<{ hasConflicts: boolean; conflicts: string[] }>({
    hasConflicts: false,
    conflicts: []
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const [initializationStep, setInitializationStep] = useState(0);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderCategory, setNewFolderCategory] = useState('');
  const [mailAccounts, setMailAccounts] = useState<MailAccount[]>([]);
  const [emailAutomationPreview, setEmailAutomationPreview] = useState<MatterEmailAutomationPreview | null>(null);
  const [emailAutomationStatus, setEmailAutomationStatus] = useState<string | null>(null);
  const [newParticipantName, setNewParticipantName] = useState('');
  const [newParticipantEmail, setNewParticipantEmail] = useState('');
  const [availableTaskLists, setAvailableTaskLists] = useState<Array<{
    id: string; name: string; description?: string | null;
    jurisdiction_code?: string | null; case_subtype?: string | null;
    case_subtype_local_label?: string | null;
    template_kind?: string; lifecycle_status?: string;
    parent_template_id?: string | null; template_family_key?: string | null;
    total_task_count?: number; total_form_count?: number; total_phase_count?: number;
    confidence_score?: string | null;
    phases?: Array<{ name: string; order: number; task_count: number; form_numbers: string[] }>;
  }>>([]);
  const [dynamicPracticeAreas, setDynamicPracticeAreas] = useState<string[]>([]);
  const [dynamicCourts, setDynamicCourts] = useState<Array<{ jurisdiction_code: string; court_name: string }>>([]);
  const [templatePreview, setTemplatePreview] = useState<any>(null);
  const [previewPanelTemplateId, setPreviewPanelTemplateId] = useState<string | null>(null);
  const [autoAddedTemplateIds, setAutoAddedTemplateIds] = useState<Map<string, { subtypeLabel: string; phaseCount: number; taskCount: number }>>(new Map());
  const [courtSnapshot, setCourtSnapshot] = useState<{ courtName: string | null; feeCount: number; judgeCount: number; mandatedDeadlines: number } | null>(null);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const isMountedRef = useRef(true);

  // Helper function to ensure date is a Date object
  const ensureDate = (date: Date | string | undefined): Date | undefined => {
    if (!date) return undefined;
    if (date instanceof Date) return date;
    if (typeof date === 'string') {
      const parsed = new Date(date);
      return isNaN(parsed.getTime()) ? undefined : parsed;
    }
    return undefined;
  };

  // Helper function to format date for form inputs
  const formatDateForInput = (date: Date | string | undefined): string => {
    const dateObj = ensureDate(date);
    return dateObj ? dateObj.toISOString().split('T')[0] : '';
  };

  // Populate form with existing matter data for edit mode
  const populateFormWithMatterData = (matter: MatterDetailed) => {
    const populatedData: MatterCreateFormData = {
      template: {
        useTemplate: Boolean(matter.numbering_template_id),
        selectedTemplateId: matter.numbering_template_id || undefined,
        availableTemplates: [] // Will be filled by template loading step
      },
      details: {
        clientId: matter.client_id || '',
        description: matter.description || matter.title || '',
        responsibleAttorneyId: matter.responsible_attorney_id || matter.responsibleAttorney?.id || undefined,
        originatingAttorneyId: matter.originating_attorney_id || matter.originatingAttorney?.id || undefined,
        responsibleStaffId: matter.responsible_staff_id || matter.responsibleStaff?.id || undefined,
        clientReferenceNumber: matter.matter_number || '',
        stateCode: matter.jurisdiction_code ? matter.jurisdiction_code.split('-')[0] : undefined,
        practiceAreaId: matter.practice_area || '',
        jurisdictionCode: matter.jurisdiction_code || undefined,
        caseSubtype: matter.case_subtype || undefined,
        plaintiffPetitioner: matter.plaintiff_petitioner || undefined,
        defendantRespondent: matter.defendant_respondent || undefined,
        opposingAttorneys: Array.isArray(matter.custom_fields?.opposingAttorneys)
          ? (matter.custom_fields.opposingAttorneys as OpposingAttorney[])
          : [],
        stageId: (matter.custom_fields?.litigationStageId as string) || '',
        status: (matter.status as MatterStatus) || 'active',
      },
      caseNumbers: (matter.custom_fields?.caseNumbers as CaseNumberEntry[]) || [],
      relatedProceedings: {
        relatedProceedings: (matter.related_proceedings || []).map((entry: RelatedProceeding) => ({
          proceeding_id: entry.proceeding_id,
          relationship_type: entry.relationship_type,
          title: entry.title,
          style_of_case: entry.style_of_case || '',
          practice_area: entry.practice_area || '',
          jurisdiction_code: entry.jurisdiction_code,
          court_name: entry.court_name || '',
          case_number: entry.case_number || '',
          filing_date: entry.filing_date || '',
          status: entry.status,
          role: entry.role || '',
          summary: entry.summary || '',
          strategic_importance: entry.strategic_importance,
          affects_deadlines: entry.affects_deadlines,
          affects_conflicts: entry.affects_conflicts,
        }))
      },
      permissions: {
        accessLevel: matter.access_level || 'everyone',
        allowedUserIds: matter.allowed_user_ids || [],
        allowedGroupIds: matter.allowed_group_ids || []
      },
      notifications: {
        notificationUserIds: (matter.custom_fields?.notificationUserIds as string[]) || []
      },
      blockUsers: {
        blockedUserIds: matter.blocked_user_ids || []
      },
      relatedContacts: {
        relatedContacts: (matter.custom_fields?.relatedContacts as RelatedContact[]) || []
      },
      customFields: {
        fieldSetIds: [],
        customFields: []
      },
      billing: {
        isBillable: matter.isBillable !== undefined ? matter.isBillable : true,
        billingMethod: (matter.billingMethod as BillingMethod) || 'Hourly',
        customRates: [],
        budget: matter.budget?.total_budget || undefined,
        budgetAlertThresholds: matter.budget?.alert_thresholds || [],
        enableSplitBilling: !!matter.splitBilling,
        splitAllocationRules: matter.splitBilling?.rules || [],
        retainerConfig: {
          enabled: !!matter.retainer,
          minimum_threshold: matter.retainer?.minimum_threshold || 0,
          replenish_amount: matter.retainer?.replenish_amount || 0,
          auto_replenish: matter.retainer?.auto_replenish || false,
          notify_on_low_balance: matter.retainer?.notify_on_low_balance ?? true,
        },
      },
      taskLists: {
        taskListIds: (matter.custom_fields?.taskListIds as string[]) || [],
        notifyAssignees: true,
        dueDateOffsets: {}
      },
      documentFolders: {
        folders: [],
        emailAutomation: defaultMatterEmailAutomation(),
      },
      reports: {
        originatingAttorneyAllocation: 100,
        useFirmSettingsOriginating: true,
        responsibleAttorneyAllocation: 100,
        useFirmSettingsResponsible: true
      }
    };

    setFormData(populatedData);
    setOriginalData(JSON.parse(JSON.stringify(populatedData))); // Deep copy for change tracking
  };

  // Calculate what has changed for user feedback
  const calculateChanges = (original: MatterCreateFormData, current: MatterCreateFormData): Array<{field: string, oldValue: any, newValue: any}> => {
    const changes: Array<{field: string, oldValue: any, newValue: any}> = [];
    
    // Check details section changes
    const details = current.details;
    const originalDetails = original.details;
    
    // Description
    if (details.description !== originalDetails.description) {
      changes.push({
        field: 'Description',
        oldValue: originalDetails.description || '(empty)',
        newValue: details.description || '(empty)'
      });
    }
    
    // Client - Show contact names instead of IDs
    if (details.clientId !== originalDetails.clientId) {
      const getClientName = (clientId: string) => {
        if (!clientId) return '(no client selected)';
        const contact = contacts.find(c => c.id === clientId);
        return contact ? `${contact.name} ${contact.contact_type === 'company' ? '(Company)' : '(Individual)'}` : `Unknown Client (${clientId})`;
      };
      
      changes.push({
        field: 'Client',
        oldValue: getClientName(originalDetails.clientId),
        newValue: getClientName(details.clientId)
      });
    }
    
    // Matter Number 
    if (details.clientReferenceNumber !== originalDetails.clientReferenceNumber) {
      changes.push({
        field: 'Engagement Number',
        oldValue: originalDetails.clientReferenceNumber || '(empty)',
        newValue: details.clientReferenceNumber || '(empty)'
      });
    }
    
    // Practice Area
    if (details.practiceAreaId !== originalDetails.practiceAreaId) {
      changes.push({
        field: 'Practice Area',
        oldValue: originalDetails.practiceAreaId || '(none selected)',
        newValue: details.practiceAreaId || '(none selected)'
      });
    }
    
    // Budget - Format as currency
    if (current.billing.budget !== original.billing.budget) {
      const formatBudget = (value: number | undefined) => {
        if (!value || value === 0) return '(no budget set)';
        return `$${value.toLocaleString()}`;
      };
      
      changes.push({
        field: 'Budget',
        oldValue: formatBudget(original.billing.budget),
        newValue: formatBudget(current.billing.budget)
      });
    }
    
    // Status
    if (details.status !== originalDetails.status) {
      const formatStatus = (status: string) => {
        const statusLabels: Record<string, string> = {
          'active': 'Active',
          'closed': 'Closed',
          'on_hold': 'On Hold',
          'pending': 'Pending'
        };
        return statusLabels[status] || status;
      };
      
      changes.push({
        field: 'Status',
        oldValue: formatStatus(originalDetails.status),
        newValue: formatStatus(details.status)
      });
    }
    
    // Phase 1 Quick Wins: Add change tracking for newly supported fields
    
    // Responsible Attorney
    if (details.responsibleAttorneyId !== originalDetails.responsibleAttorneyId) {
      const getAttorneyName = (attorneyId: string | undefined) => {
        if (!attorneyId) return '(no attorney assigned)';
        const attorney = attorneys.find(a => a.id === attorneyId);
        return attorney ? attorney.name : `Unknown Attorney (${attorneyId})`;
      };
      
      changes.push({
        field: 'Responsible Attorney',
        oldValue: getAttorneyName(originalDetails.responsibleAttorneyId),
        newValue: getAttorneyName(details.responsibleAttorneyId)
      });
    }
    
    // 🆕 FIX: Check for case number changes
    const originalCaseNumbers = original.caseNumbers || [];
    const currentCaseNumbers = current.caseNumbers || [];
    
    // Check if case numbers array length changed
    if (originalCaseNumbers.length !== currentCaseNumbers.length) {
      changes.push({
        field: 'Case Numbers',
        oldValue: `${originalCaseNumbers.length} case number(s)`,
        newValue: `${currentCaseNumbers.length} case number(s)`
      });
    } else {
      // Check if any case number content changed
      for (let i = 0; i < currentCaseNumbers.length; i++) {
        const originalCase = originalCaseNumbers[i] || {};
        const currentCase = currentCaseNumbers[i] || {};
        
        if (originalCase.caseNumber !== currentCase.caseNumber ||
            originalCase.court !== currentCase.court ||
            originalCase.jurisdiction !== currentCase.jurisdiction ||
            originalCase.caseType !== currentCase.caseType ||
            originalCase.status !== currentCase.status ||
            originalCase.filedDate !== currentCase.filedDate) {
          
          changes.push({
            field: `Case Number ${i + 1}`,
            oldValue: originalCase.caseNumber || '(empty)',
            newValue: currentCase.caseNumber || '(empty)'
          });
          break; // Only show first change to avoid spam
        }
      }
    }

    const originalOpposingAttorneys = original.details.opposingAttorneys || [];
    const currentOpposingAttorneys = current.details.opposingAttorneys || [];
    if (JSON.stringify(originalOpposingAttorneys) !== JSON.stringify(currentOpposingAttorneys)) {
      changes.push({
        field: 'Opposing Attorney',
        oldValue: `${originalOpposingAttorneys.length} contact(s)`,
        newValue: `${currentOpposingAttorneys.length} contact(s)`
      });
    }

    const originalProceedings = original.relatedProceedings?.relatedProceedings || [];
    const currentProceedings = current.relatedProceedings?.relatedProceedings || [];
    if (JSON.stringify(originalProceedings) !== JSON.stringify(currentProceedings)) {
      changes.push({
        field: 'Related Proceedings',
        oldValue: `${originalProceedings.length} proceeding(s)`,
        newValue: `${currentProceedings.length} proceeding(s)`
      });
    }

    if (JSON.stringify(original.documentFolders.emailAutomation) !== JSON.stringify(current.documentFolders.emailAutomation)) {
      changes.push({
        field: 'EdgeMail communications',
        oldValue: original.documentFolders.emailAutomation.enabled
          ? `${original.documentFolders.emailAutomation.participants.length} participant(s)`
          : 'Disabled',
        newValue: current.documentFolders.emailAutomation.enabled
          ? `${current.documentFolders.emailAutomation.participants.length} participant(s)`
          : 'Disabled',
      });
    }
    
    return changes;
  };

  const buildRelatedProceedingPayload = (entry: RelatedProceedingDraft) => ({
    relationship_type: entry.relationship_type,
    title: entry.title.trim(),
    style_of_case: entry.style_of_case?.trim() || undefined,
    practice_area: entry.practice_area?.trim() || undefined,
    jurisdiction_code: entry.jurisdiction_code.trim(),
    court_name: entry.court_name?.trim() || undefined,
    case_number: entry.case_number?.trim() || undefined,
    filing_date: entry.filing_date || undefined,
    status: entry.status,
    role: entry.role?.trim() || undefined,
    summary: entry.summary?.trim() || undefined,
    strategic_importance: entry.strategic_importance,
    affects_deadlines: entry.affects_deadlines,
    affects_conflicts: entry.affects_conflicts,
  });

  const syncRelatedProceedings = async (
    targetMatterId: string,
    currentProceedings: RelatedProceedingDraft[],
    originalProceedings: RelatedProceedingDraft[] = []
  ) => {
    const token = keycloak?.token;
    if (!token) {
      throw new Error('Missing authentication token for related proceeding sync');
    }

    const sanitizedCurrent = currentProceedings
      .filter((entry) => entry.title.trim() || entry.jurisdiction_code.trim() || entry.case_number?.trim())
      .map((entry) => ({ ...entry, title: entry.title.trim(), jurisdiction_code: entry.jurisdiction_code.trim() }));
    const originalMap = new Map(originalProceedings.filter((entry) => entry.proceeding_id).map((entry) => [entry.proceeding_id as string, entry]));
    const currentIds = new Set(sanitizedCurrent.map((entry) => entry.proceeding_id).filter(Boolean) as string[]);

    for (const originalEntry of originalProceedings) {
      if (originalEntry.proceeding_id && !currentIds.has(originalEntry.proceeding_id)) {
        const response = await fetch(`/api/v1/matters/${targetMatterId}/related-proceedings/${originalEntry.proceeding_id}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          throw new Error(`Failed to remove related proceeding ${originalEntry.proceeding_id}`);
        }
      }
    }

    for (const entry of sanitizedCurrent) {
      const payload = buildRelatedProceedingPayload(entry);

      if (entry.proceeding_id && originalMap.has(entry.proceeding_id)) {
        const previousPayload = buildRelatedProceedingPayload(originalMap.get(entry.proceeding_id)!);
        if (JSON.stringify(payload) === JSON.stringify(previousPayload)) {
          continue;
        }

        const response = await fetch(`/api/v1/matters/${targetMatterId}/related-proceedings/${entry.proceeding_id}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          throw new Error(`Failed to update related proceeding ${entry.proceeding_id}`);
        }
      } else {
        const response = await fetch(`/api/v1/matters/${targetMatterId}/related-proceedings`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          throw new Error(`Failed to add related proceeding ${entry.title}`);
        }
      }
    }
  };

  const normalizeMatterEmailParticipants = (participants: MatterEmailAutomationParticipant[]) => {
    const seen = new Set<string>();
    return participants
      .map((participant) => ({
        name: participant.name?.trim() || null,
        email: participant.email.trim().toLowerCase(),
        role: participant.role?.trim() || null,
      }))
      .filter((participant) => {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(participant.email)) return false;
        if (seen.has(participant.email)) return false;
        seen.add(participant.email);
        return true;
      });
  };

  const showNotice = (title: string, message: string, tone: NoticeModalState['tone'] = 'info') => {
    setNoticeModal({ title, message, tone });
  };

  const buildMatterEmailAutomationPayload = (matter: Pick<MatterDetailed, 'id' | 'matter_number'>) => {
    const config = formData.documentFolders.emailAutomation;
    return {
      matter_id: matter.id,
      matter_number: matter.matter_number || formData.details.clientReferenceNumber || matter.id,
      mailbox_id: config.mailboxId || null,
      enabled: config.enabled,
      participants: normalizeMatterEmailParticipants(config.participants),
      include_existing: config.includeExisting,
      create_future_rule: config.createFutureRule,
    };
  };

  const fetchMatterEmailAutomation = async (targetMatterId: string) => {
    const token = keycloak?.token;
    if (!token) return;
    const response = await fetch(`/api/v1/matters/${targetMatterId}/email-automation`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) return;
    const data = await response.json();
    const automation = data.automation;
    if (!automation) return;
    const nextConfig: MatterEmailAutomationSection = {
      enabled: automation.isEnabled === true,
      mailboxId: automation.mailboxId || '',
      participants: Array.isArray(automation.participants) ? automation.participants : [],
      includeExisting: true,
      createFutureRule: Boolean(automation.ruleId),
    };
    setFormData(prev => ({
      ...prev,
      documentFolders: {
        ...prev.documentFolders,
        emailAutomation: nextConfig,
      },
    }));
    setOriginalData(prev => prev ? ({
      ...prev,
      documentFolders: {
        ...prev.documentFolders,
        emailAutomation: nextConfig,
      },
    }) : prev);
    setEmailAutomationPreview({
      enabled: nextConfig.enabled,
      mailboxId: nextConfig.mailboxId,
      targetFolderPath: automation.targetFolderPath,
      participantEmails: nextConfig.participants.map((p) => p.email),
      existingMatchCount: automation.lastPreviewCount || 0,
      outsideTargetCount: 0,
      samples: [],
      binding: {
        isEnabled: automation.isEnabled,
        backfillStatus: automation.backfillStatus,
        lastMovedCount: automation.lastMovedCount || 0,
        targetFolderPath: automation.targetFolderPath,
      },
    });
  };

  const previewMatterEmailAutomation = async () => {
    if (!matterId) {
      setEmailAutomationStatus('Preview is available after the engagement is saved.');
      return;
    }
    const token = keycloak?.token;
    if (!token) return;
    setEmailAutomationStatus('Previewing matching mail...');
    const payload = buildMatterEmailAutomationPayload({
      id: matterId,
      matter_number: existingMatter?.matter_number || formData.details.clientReferenceNumber || matterId,
    });
    const response = await fetch('/api/v1/matters/email-automation/preview', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error(await readApiErrorMessage(response, 'Failed to preview engagement email automation'));
    }
    const preview = await response.json();
    setEmailAutomationPreview(preview);
    setEmailAutomationStatus(null);
  };

  const syncMatterEmailAutomation = async (matter: Pick<MatterDetailed, 'id' | 'matter_number'>) => {
    const token = keycloak?.token;
    const config = formData.documentFolders.emailAutomation;
    if (!token || (!config.enabled && !originalData?.documentFolders.emailAutomation.enabled)) return;
    const payload = buildMatterEmailAutomationPayload(matter);
    const response = await fetch(`/api/v1/matters/${matter.id}/email-automation`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error(await readApiErrorMessage(response, 'Failed to sync engagement email automation'));
    }
    const result = await response.json();
    setEmailAutomationPreview({
      enabled: result.isEnabled,
      mailboxId: result.mailboxId,
      targetFolderPath: result.targetFolderPath,
      participantEmails: result.participants?.map((p: MatterEmailAutomationParticipant) => p.email) || [],
      existingMatchCount: result.existingMatchCount || 0,
      outsideTargetCount: result.outsideTargetCount || 0,
      samples: [],
      binding: {
        isEnabled: result.isEnabled,
        backfillStatus: result.backfillStatus,
        lastMovedCount: result.lastMovedCount || 0,
        targetFolderPath: result.targetFolderPath,
      },
    });
  };

  // Confirm and save the changes after user approval
  const confirmAndSaveChanges = async () => {
    if (!matterId) return;

    try {
      // Update matter via API
      if (pendingUpdates && Object.keys(pendingUpdates).length > 0) {
        await updateMatter(matterId, pendingUpdates);
      }
      await syncRelatedProceedings(
        matterId,
        formData.relatedProceedings.relatedProceedings,
        originalData?.relatedProceedings?.relatedProceedings || []
      );
      await syncMatterEmailAutomation({
        id: matterId,
        matter_number: existingMatter?.matter_number || formData.details.clientReferenceNumber || matterId,
      });
      
      // Close modal and navigate back to engagement view
      setShowChangeModal(false);
      setPendingUpdates(null);
      navigate(`/engagements/${matterId}`);
      
    } catch (error) {
      console.error('Failed to update matter:', error);
      showNotice(
        'Could not save engagement',
        messageFromUnknownError(error, 'Failed to update engagement. Please try again.'),
        'error',
      );
    }
  };

  // Static fallback practice areas — must match canonical displayNames from practice_areas seed
  const FALLBACK_PRACTICE_AREAS = [
    'Civil Litigation', 'Corporate Litigation', 'Criminal Law', 'Employment Law',
    'Estate Planning', 'Family Law', 'Immigration Law', 'Intellectual Property',
    'Personal Injury', 'Real Estate',
  ];

  // Use firm-configured practice areas; fall back to static list if API returns nothing
  const practiceAreas = useMemo(() => {
    if (dynamicPracticeAreas.length === 0) return FALLBACK_PRACTICE_AREAS;
    return dynamicPracticeAreas;
  }, [dynamicPracticeAreas]);

  // US States list for the State dropdown
  const US_STATES = [
    { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }, { code: 'DC', name: 'District of Columbia' },
  ];

  const matterStages = [
    'Initial Consultation', 'Discovery', 'Negotiation', 'Litigation', 'Settlement', 'Trial'
  ];

  // Use real attorneys from User Management system
  const attorneys = realAttorneys || [];

  // ✅ SECURITY ENHANCEMENT: Realm-aware data filtering
  const filteredContacts = useMemo(() => {
    try {
      const currentRealm = getCurrentRealm();
      return contacts.filter(contact => {
        if (!contact.realm_id) {
          console.warn('🔒 [Security] Contact missing realm_id:', contact.id);
          return false;
        }
        return contact.realm_id === currentRealm;
      });
    } catch (error) {
      console.error('🔒 [Security] Failed to filter contacts by realm:', error);
      return [];
    }
  }, [contacts, getCurrentRealm]);

  const filteredAttorneys = useMemo(() => {
    console.log('🔍 [MatterCreateForm] Processing attorneys for enhanced name display...');
    
    // Enhanced attorney processing with robust name extraction (like Transfer modal)
    return attorneys
      .filter(attorney => {
        // All attorneys from the authenticated API should be valid for the current realm
        return attorney.id;
      })
      .map(attorney => {
        // Extract name from various possible formats (same logic as Transfer modal)
        let firstName = 'Unknown';
        let lastName = 'User';
        let displayName = attorney.name || 'Unknown User';
        
        if (attorney.name && attorney.name !== 'Unknown User') {
          // Use existing name if it's already processed
          displayName = attorney.name;
        } else {
          // Enhanced name extraction from raw attorney data
          const rawAttorney = attorney as any; // Access raw fields
          
          if (rawAttorney.first_name && rawAttorney.last_name) {
            firstName = rawAttorney.first_name;
            lastName = rawAttorney.last_name;
            displayName = `${firstName} ${lastName}`;
          } else if (rawAttorney.firstName && rawAttorney.lastName) {
            firstName = rawAttorney.firstName;
            lastName = rawAttorney.lastName;
            displayName = `${firstName} ${lastName}`;
          } else if (rawAttorney.name && rawAttorney.name !== attorney.name) {
            // Use raw name field if different from processed name
            displayName = rawAttorney.name;
          } else if (rawAttorney.username) {
            // Use username as fallback
            displayName = rawAttorney.username;
          } else if (attorney.email) {
            // Extract name from email as last resort
            const emailName = attorney.email.split('@')[0];
            displayName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
          }
        }
        
        console.log(`🔍 [MatterCreateForm] Attorney processed:`, {
          id: attorney.id,
          originalName: attorney.name,
          finalDisplayName: displayName,
          email: attorney.email,
          roles: attorney.roles
        });
        
        return {
          ...attorney,
          name: displayName,
          displayName: displayName
        };
      });
  }, [attorneys]);

  // 🆕 ADMINISTRATIVE GROUP DETECTION: Smart defaults for responsible staff
  const administrativeUsers = useMemo(() => {
    return filteredAttorneys.filter(attorney => {
      // Check if user has administrative roles
      const roles = attorney.roles || [];
      return roles.some(role => 
        role.toLowerCase().includes('admin') || 
        role.toLowerCase().includes('assistant') ||
        role.toLowerCase().includes('paralegal') ||
        role.toLowerCase().includes('billing')
      );
    });
  }, [filteredAttorneys]);

  const suggestedResponsibleStaff = useMemo(() => {
    // Priority order for responsible staff suggestions:
    // 1. Administrative users
    // 2. Users with 'admin' role specifically  
    // 3. The current user (creator)
    // 4. First available user
    
    if (administrativeUsers.length > 0) {
      // Prefer users with explicit 'admin' role
      const adminUsers = administrativeUsers.filter(user => 
        user.roles?.some(role => role.toLowerCase() === 'admin')
      );
      if (adminUsers.length > 0) {
        return adminUsers[0];
      }
      return administrativeUsers[0];
    }
    
    return filteredAttorneys[0]; // Fallback to first available user
  }, [administrativeUsers, filteredAttorneys]);

  // NOTE: Auto-default useEffect moved after updateSection function definition

  // 🆕 REQUIRED FIELDS TRACKING: Visual queue for required field completion
  const requiredFieldsStatus = useMemo(() => {
    return {
      client: {
        completed: !!formData.details.clientId,
        label: 'Select Client',
        section: 'details'
      },
      description: {
        completed: !!formData.details.description?.trim(),
        label: 'Engagement Description',
        section: 'details'
      },
      responsibleStaff: {
        completed: !!formData.details.responsibleStaffId,
        label: 'Responsible Staff',
        section: 'details'
      }
    };
  }, [formData.details.clientId, formData.details.description, formData.details.responsibleStaffId]);

  const totalRequired = Object.keys(requiredFieldsStatus).length;
  const completedRequired = Object.values(requiredFieldsStatus).filter(field => field.completed).length;
  const isFormReadyToSubmit = completedRequired === totalRequired;

  // Coordinated initialization to prevent race conditions
  useEffect(() => {
    const initializeForm = async () => {
      try {
        // ✅ SECURITY ENHANCEMENT: Validate realm context first
        try {
          validateRealmContext();
          console.log('🔒 [Security] Realm context validated for matter form');
        } catch (error) {
          console.error('🔒 [Security] Realm validation failed:', error);
          navigate('/engagements');
          return;
        }

        // Step 1: Load contacts data
        if (isMountedRef.current) setInitializationStep(1);
        await fetchContacts({});
        try {
          const accounts = await fetchMailboxAccounts();
          if (isMountedRef.current) {
            setMailAccounts(accounts);
            if (accounts.length > 0) {
              setFormData(prev => ({
                ...prev,
                documentFolders: {
                  ...prev.documentFolders,
                  emailAutomation: {
                    ...prev.documentFolders.emailAutomation,
                    mailboxId: prev.documentFolders.emailAutomation.mailboxId || accounts[0].id,
                  },
                },
              }));
            }
          }
        } catch (mailboxError) {
          console.warn('[MatterCreateForm] Failed to load WeBeMail mailboxes:', mailboxError);
        }

        // Step 2: Load existing matter data in edit mode
        if (isEditMode && matterId) {
          if (isMountedRef.current) setInitializationStep(2);
          setIsLoadingMatter(true);
          try {
            const matter = await fetchMatterById(matterId);
            if (matter && isMountedRef.current) {
              // ✅ SECURITY ENHANCEMENT: Validate matter belongs to current realm
              const currentRealm = getCurrentRealm();
              if (matter.realm_id !== currentRealm) {
                console.error('🔒 [Security] UNAUTHORIZED MATTER ACCESS ATTEMPT:', {
                  matterId,
                  matterRealm: matter.realm_id,
                  userRealm: currentRealm
                });
                navigate('/engagements');
                return;
              }
              
              setExistingMatter(matter);
              populateFormWithMatterData(matter);
              await fetchMatterEmailAutomation(matter.id);

              // Fetch billing snapshot from dedicated endpoint to get real entity data
              try {
                const token = keycloak?.token;
                if (token) {
                  const billingRes = await fetch(`/api/v1/matters/${matterId}/billing`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  if (billingRes.ok) {
                    const billingSnapshot = await billingRes.json();
                    // Merge billing entity data into form state
                    if (isMountedRef.current) {
                      setFormData(prev => ({
                        ...prev,
                        billing: {
                          ...prev.billing,
                          budget: billingSnapshot.budget?.total_budget || prev.billing.budget,
                          budgetAlertThresholds: billingSnapshot.budget?.alert_thresholds || [],
                          enableSplitBilling: !!billingSnapshot.split_billing,
                          splitAllocationRules: billingSnapshot.split_billing?.rules || [],
                          retainerConfig: {
                            enabled: !!billingSnapshot.retainer,
                            minimum_threshold: billingSnapshot.retainer?.minimum_threshold || 0,
                            replenish_amount: billingSnapshot.retainer?.replenish_amount || 0,
                            auto_replenish: billingSnapshot.retainer?.auto_replenish || false,
                            notify_on_low_balance: billingSnapshot.retainer?.notify_on_low_balance ?? true,
                          },
                        },
                      }));
                      // Also update originalData so diff comparison works correctly
                      setOriginalData(prev => prev ? ({
                        ...prev,
                        billing: {
                          ...prev.billing,
                          budget: billingSnapshot.budget?.total_budget || prev.billing.budget,
                          budgetAlertThresholds: billingSnapshot.budget?.alert_thresholds || [],
                          enableSplitBilling: !!billingSnapshot.split_billing,
                          splitAllocationRules: billingSnapshot.split_billing?.rules || [],
                          retainerConfig: {
                            enabled: !!billingSnapshot.retainer,
                            minimum_threshold: billingSnapshot.retainer?.minimum_threshold || 0,
                            replenish_amount: billingSnapshot.retainer?.replenish_amount || 0,
                            auto_replenish: billingSnapshot.retainer?.auto_replenish || false,
                            notify_on_low_balance: billingSnapshot.retainer?.notify_on_low_balance ?? true,
                          },
                        },
                      }) : prev);
                    }
                  }
                }
              } catch (billingErr) {
                console.warn('[MatterCreateForm] Failed to fetch billing snapshot:', billingErr);
              }
            }
          } catch (error) {
            console.error('Failed to load matter for editing:', error);
            // Still continue with initialization but show error
          } finally {
            if (isMountedRef.current) setIsLoadingMatter(false);
          }
        }

        // Step 3: Initialize templates
        if (isMountedRef.current) setInitializationStep(isEditMode ? 3 : 2);
        const templates: MatterTemplate[] = await getMatterTemplates();

        // Step 4: Update form data with templates (both create and edit modes)
        if (isMountedRef.current) {
          setInitializationStep(3);
          setFormData(prev => ({
            ...prev,
            template: {
              ...prev.template,
              availableTemplates: templates,
              // In edit mode, preserve the template selection already set by populateFormWithMatterData
              ...(isEditMode ? {} : {})
            }
          }));
        }

        // Step 5: Clear any stale draft (new matter always starts blank)
        if (!isEditMode && isMountedRef.current) {
          setInitializationStep(4);
          localStorage.removeItem('webelieve-engagement-draft');
        }

        // Mark as initialized
        if (isMountedRef.current) setIsInitialized(true);
      } catch (error) {
        console.error('Error initializing form:', error);
        if (isMountedRef.current) setIsInitialized(true); // Still mark as initialized to prevent infinite loading
      }
    };

    if (!isInitialized) {
      initializeForm();
    }

    // Cleanup function to mark component as unmounted
    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized]); // Only depend on isInitialized to prevent loops

  useEffect(() => {
    if (mailAccounts.length === 0 || formData.documentFolders.emailAutomation.mailboxId) return;
    const nextMailboxId = mailAccounts[0].id;
    setFormData(prev => ({
      ...prev,
      documentFolders: {
        ...prev.documentFolders,
        emailAutomation: {
          ...prev.documentFolders.emailAutomation,
          mailboxId: nextMailboxId,
        },
      },
    }));
    setOriginalData(prev => prev ? ({
      ...prev,
      documentFolders: {
        ...prev.documentFolders,
        emailAutomation: {
          ...prev.documentFolders.emailAutomation,
          mailboxId: prev.documentFolders.emailAutomation.mailboxId || nextMailboxId,
        },
      },
    }) : prev);
  }, [mailAccounts, formData.documentFolders.emailAutomation.mailboxId]);

  // Fetch task templates from API — filtered by selected practice area (enriched mode)
  // Uses local `cancelled` flag instead of shared isMountedRef (which gets stuck at false)
  const selectedPracticeArea = formData.details.practiceAreaId;
  useEffect(() => {
    const token = keycloak?.token;
    if (!token) return;
    if (!selectedPracticeArea) {
      setAvailableTaskLists([]);
      return;
    }
    let cancelled = false;
    const qs = new URLSearchParams({
      practice_area: selectedPracticeArea,
      enriched: 'true',
    });
    // Letter-for-letter fidelity: pass the matter's jurisdiction so the
    // backend can resolve each template's canonical case_subtype into the
    // jurisdiction's local_label (e.g. "divorce" in GA, "dissolution" in CA).
    const juri = formData.details.jurisdictionCode;
    const state = formData.details.stateCode;
    if (juri) qs.set('jurisdiction_code', juri);
    if (state) qs.set('state_code', state);
    const url = `/api/v1/task-templates?${qs.toString()}`;
    fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.ok ? res.json() : Promise.reject(res))
      .then(data => {
        if (!cancelled && Array.isArray(data?.templates)) {
          setAvailableTaskLists(data.templates.map((t: any) => ({
            id: t.id,
            name: t.name,
            description: t.description || null,
            jurisdiction_code: t.jurisdiction_code || null,
            case_subtype: t.case_subtype || null,
            case_subtype_local_label: t.case_subtype_local_label || null,
            template_kind: t.template_kind || 'base',
            lifecycle_status: t.lifecycle_status || 'draft',
            parent_template_id: t.parent_template_id || null,
            template_family_key: t.template_family_key || null,
            total_task_count: t.total_task_count || 0,
            total_form_count: t.total_form_count || 0,
            total_phase_count: t.total_phase_count || 0,
            confidence_score: t.confidence_score || null,
            phases: t.phases || [],
          })));
        }
      })
      .catch(() => { if (!cancelled) setAvailableTaskLists([]); });
    return () => { cancelled = true; };
  }, [keycloak?.token, selectedPracticeArea]);

  // Fetch court intelligence snapshot for the Task Lists section (Sprint 4H)
  const snapshotJurisdiction = formData.details.jurisdictionCode;
  useEffect(() => {
    if (!snapshotJurisdiction || !selectedPracticeArea) {
      setCourtSnapshot(null);
      return;
    }
    let cancelled = false;
    courtIntelligenceApi
      .getPreview({
        jurisdiction_code: snapshotJurisdiction,
        practice_area: selectedPracticeArea,
        state_code: formData.details.stateCode,
      })
      .then((data) => {
        if (!cancelled) {
          setCourtSnapshot({
            courtName: data.court_name,
            feeCount: data.fees?.total || 0,
            judgeCount: data.judges?.total || 0,
            mandatedDeadlines: data.template_summary?.best_match?.task_count || 0,
          });
        }
      })
      .catch(() => { if (!cancelled) setCourtSnapshot(null); });
    return () => { cancelled = true; };
  }, [snapshotJurisdiction, selectedPracticeArea, formData.details.stateCode]);

  // Fetch firm-configured practice areas from Firm Information settings
  // Uses local `cancelled` flag instead of shared isMountedRef (which gets stuck at false)
  useEffect(() => {
    const token = keycloak?.token;
    if (!token) return;
    let cancelled = false;
    fetch('/api/v1/dms/practice-areas/firm-specific', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.ok ? res.json() : Promise.reject(res))
      .then(data => {
        if (!cancelled && Array.isArray(data?.practiceAreas)) {
          setDynamicPracticeAreas(data.practiceAreas);
        }
      })
      .catch(() => { /* fallback to static list */ });
    return () => { cancelled = true; };
  }, [keycloak?.token]);

  // Fetch courts when stateCode changes (for Engagement Details Court Jurisdiction Code dropdown)
  // Uses local `cancelled` flag instead of shared isMountedRef (which gets stuck at false)
  const selectedStateCode = formData.details.stateCode;
  useEffect(() => {
    const token = keycloak?.token;
    if (!token || !selectedStateCode) {
      setDynamicCourts([]);
      return;
    }
    let cancelled = false;
    fetch(`/api/v1/court-registry/courts?state_code=${encodeURIComponent(selectedStateCode)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.ok ? res.json() : Promise.reject(res))
      .then(data => {
        if (!cancelled && Array.isArray(data?.courts)) {
          setDynamicCourts(data.courts);
        }
      })
      .catch(() => {
        if (!cancelled) setDynamicCourts([]);
      });
    return () => { cancelled = true; };
  }, [keycloak?.token, selectedStateCode]);

  // Auto-save functionality (EdgeLex standout feature) - only in create mode after initialization
  useEffect(() => {
    if (!isEditMode && isDirty && isInitialized) {
      setIsAutoSaving(true);
      const timer = setTimeout(() => {
        // Simulate auto-save
        localStorage.setItem('webelieve-engagement-draft', JSON.stringify(formData));
        if (isMountedRef.current) {
          setIsAutoSaving(false);
          setIsDirty(false);
        }
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [isEditMode, isDirty, isInitialized, formData]);

  // Conflict checking via backend query - only after initialization
  useEffect(() => {
    if (isInitialized && formData.details.clientId) {
      const timer = setTimeout(() => {
        const token = keycloak?.token;
        if (!token) return;
        fetch('/api/v1/matters/check-conflicts', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            client_id: formData.details.clientId,
            relatedContacts: formData.relatedContacts.relatedContacts?.map(rc => ({
              contactId: rc.contactId,
              relationship: rc.relationship,
            })),
          }),
        })
          .then(res => res.ok ? res.json() : Promise.reject(res))
          .then(data => {
            if (isMountedRef.current) {
              setConflictCheck({
                hasConflicts: data.hasConflicts ?? false,
                conflicts: data.conflicts ?? [],
              });
            }
          })
          .catch(() => {
            // Fail-open: no conflicts on error
            if (isMountedRef.current) {
              setConflictCheck({ hasConflicts: false, conflicts: [] });
            }
          });
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [isInitialized, formData.details.clientId, formData.relatedContacts.relatedContacts, keycloak?.token]);

  // Rule-based suggestions based on form state - only after initialization
  useEffect(() => {
    if (!isInitialized) return;
    const timer = setTimeout(() => {
      const rules: string[] = [];

      // Practice area detection from description keywords
      if (!formData.details.practiceAreaId && formData.details.description.length > 20) {
        const desc = formData.details.description.toLowerCase();
        const areaKeywords: Record<string, string[]> = {
          'Personal Injury': ['injury', 'accident', 'medical', 'negligence', 'slip and fall', 'malpractice'],
          'Family Law': ['divorce', 'custody', 'child support', 'alimony', 'marital', 'prenup'],
          'Corporate': ['merger', 'acquisition', 'corporate', 'shareholder', 'board', 'llc', 'incorporation'],
          'Immigration': ['visa', 'immigration', 'asylum', 'naturalization', 'deportation', 'green card'],
          'Criminal Defense': ['criminal', 'felony', 'misdemeanor', 'dui', 'assault', 'theft'],
          'Real Estate': ['property', 'real estate', 'landlord', 'tenant', 'lease', 'mortgage', 'foreclosure'],
        };
        for (const [area, keywords] of Object.entries(areaKeywords)) {
          if (keywords.some(kw => desc.includes(kw))) {
            rules.push(`Recommended practice area: ${area}`);
            break;
          }
        }
      }

      // Billing method suggestion based on practice area
      if (formData.details.practiceAreaId && formData.billing.billingMethod === 'Hourly') {
        const area = formData.details.practiceAreaId.toLowerCase();
        if (area.includes('personal injury') || area.includes('contingency')) {
          rules.push('Similar engagements typically use contingency billing');
        }
      }

      // Budget suggestion for hourly billing
      if (formData.billing.billingMethod === 'Hourly' && !formData.billing.budget && formData.billing.isBillable) {
        rules.push('Consider setting a budget alert threshold for hourly billing');
      }

      // Task list suggestion
      if (formData.taskLists.taskListIds.length === 0) {
        rules.push('Consider adding a task list template to auto-create tasks');
      }

      if (isMountedRef.current) {
        setAiSuggestions(rules);
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [
    isInitialized,
    formData.details.description,
    formData.details.practiceAreaId,
    formData.billing.billingMethod,
    formData.billing.budget,
    formData.billing.isBillable,
    formData.taskLists.taskListIds,
  ]);

  // Update form data for specific section
  const updateSection = (sectionId: string, data: Record<string, unknown>) => {
    setFormData(prev => ({
      ...prev,
      [sectionId]: { ...prev[sectionId as keyof MatterCreateFormData], ...data }
    }));
    // Only mark as dirty after initialization to prevent auto-save during setup
    if (isInitialized) {
      setIsDirty(true);
    }
  };

  // 🆕 AUTO-DEFAULT: Set responsible staff on form initialization (create mode only)
  useEffect(() => {
    if (!isEditMode && suggestedResponsibleStaff && !formData.details.responsibleStaffId && isInitialized) {
      console.log('🔧 [MatterCreateForm] Auto-setting responsible staff:', suggestedResponsibleStaff);
      updateSection('details', { responsibleStaffId: suggestedResponsibleStaff.id });
    }
  }, [suggestedResponsibleStaff, isEditMode, formData.details.responsibleStaffId, isInitialized]);

  // Case number management functions
  const updateCaseNumber = (index: number, field: keyof CaseNumberEntry, value: string) => {
    const updated = [...formData.caseNumbers];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, caseNumbers: updated }));
    if (isInitialized) {
      setIsDirty(true);
    }
  };

  const addCaseNumber = () => {
    setFormData(prev => ({
      ...prev,
      caseNumbers: [...prev.caseNumbers, {
        caseNumber: '',
        court: '',
        jurisdiction: '',
        jurisdictionCode: '',
        caseType: '',
        status: '',
        filedDate: ''
      }]
    }));
    if (isInitialized) {
      setIsDirty(true);
    }
  };

  const removeCaseNumber = (index: number) => {
    const updated = formData.caseNumbers.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, caseNumbers: updated }));
    if (isInitialized) {
      setIsDirty(true);
    }
  };

  const sanitizeOpposingAttorneys = (attorneys: OpposingAttorney[]) =>
    attorneys
      .map((attorney) => ({
        firstName: attorney.firstName.trim(),
        lastName: attorney.lastName.trim(),
        email: attorney.email.trim().toLowerCase(),
      }))
      .filter((attorney) => attorney.firstName || attorney.lastName || attorney.email)
      .slice(0, 5);

  const updateOpposingAttorney = (index: number, field: keyof OpposingAttorney, value: string) => {
    const updated = [...formData.details.opposingAttorneys];
    updated[index] = { ...updated[index], [field]: value };
    updateSection('details', { opposingAttorneys: updated });
  };

  const addOpposingAttorney = () => {
    if (formData.details.opposingAttorneys.length >= 5) return;
    updateSection('details', {
      opposingAttorneys: [
        ...formData.details.opposingAttorneys,
        { firstName: '', lastName: '', email: '' },
      ],
    });
  };

  const removeOpposingAttorney = (index: number) => {
    updateSection('details', {
      opposingAttorneys: formData.details.opposingAttorneys.filter((_, i) => i !== index),
    });
  };

  // Validate section and mark as completed
  const validateSection = (sectionId: string): boolean => {
    const errors: string[] = [];
    
    switch (sectionId) {
      case 'details':
        if (!formData.details.clientId) errors.push('Client is required');
        if (!formData.details.description.trim()) errors.push('Engagement description is required');
        // Case numbers validation (embedded in details — optional overall)
        formData.caseNumbers.forEach((entry, index) => {
          if (entry.caseNumber && !entry.caseNumber.trim()) {
            errors.push(`Case number ${index + 1} cannot be empty`);
          }
          if ((entry.court || entry.jurisdiction || entry.caseType || entry.status || entry.filedDate) && !entry.caseNumber.trim()) {
            errors.push(`Case number is required for entry ${index + 1} when other fields are filled`);
          }
        });
        formData.details.opposingAttorneys.forEach((attorney, index) => {
          const hasAnyValue = Boolean(attorney.firstName.trim() || attorney.lastName.trim() || attorney.email.trim());
          if (hasAnyValue && (!attorney.firstName.trim() || !attorney.lastName.trim())) {
            errors.push(`Opposing attorney ${index + 1} requires first and last name`);
          }
          if (hasAnyValue && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(attorney.email.trim())) {
            errors.push(`Opposing attorney ${index + 1} requires a valid email address`);
          }
        });
        break;
      case 'relatedProceedings':
        formData.relatedProceedings.relatedProceedings.forEach((entry, index) => {
          const hasAnyValue = Boolean(
            entry.title.trim() ||
            entry.jurisdiction_code.trim() ||
            entry.case_number?.trim() ||
            entry.court_name?.trim() ||
            entry.summary?.trim()
          );
          if (hasAnyValue && !entry.title.trim()) {
            errors.push(`Related proceeding ${index + 1} needs a title`);
          }
          if (hasAnyValue && !entry.jurisdiction_code.trim()) {
            errors.push(`Related proceeding ${index + 1} needs a jurisdiction`);
          }
        });
        break;
    }
    
    setValidationErrors(prev => ({ ...prev, [sectionId]: errors }));
    
    if (errors.length === 0) {
      setCompletedSections(prev => new Set(prev).add(sectionId));
      return true;
    }
    
    return false;
  };

  // Navigation between sections
  const navigateToSection = (sectionId: string) => {
    validateSection(currentSection);
    setCurrentSection(sectionId);
  };

  // Handle form submission
  const handleSubmit = async () => {
    // In edit mode, skip comprehensive validation - allow partial updates
    // In create mode, validate all sections to ensure complete matter creation
    const shouldValidateAll = !isEditMode;
    let allValid = shouldValidateAll ? FORM_SECTIONS.every(section => validateSection(section.id)) : true;
    
    // In edit mode, perform minimal validation only on fields being changed
    if (isEditMode && originalData) {
      const errors: string[] = [];
      
      // Only validate fields that have actual values (not empty fields that are being cleared)
      if (formData.details.description && formData.details.description.trim() === '') {
        errors.push('Engagement description cannot be empty if provided');
      }
      
      if (errors.length > 0) {
        showNotice('Review engagement details', errors.join('\n'), 'error');
        allValid = false;
      }
    }
    
    if (allValid) {
      try {
        const automationConfig = formData.documentFolders.emailAutomation;
        if (automationConfig.enabled) {
          if (!automationConfig.mailboxId) {
            showNotice('Select a mailbox', 'Select a WeBeMail mailbox for engagement communications.', 'error');
            return;
          }
          if (normalizeMatterEmailParticipants(automationConfig.participants).length === 0) {
            showNotice(
              'Add a communication participant',
              'Add at least one communication participant email before enabling engagement email automation.',
              'error',
            );
            return;
          }
        }

        if (isEditMode && matterId && originalData) {
          // Edit Mode: Update existing matter
          // Build UpdateMatterDto matching the expanded backend contract
          const updates: Record<string, any> = {};

          // --- Details section ---
          if (formData.details.clientId !== originalData.details.clientId) {
            updates.client_id = formData.details.clientId;
          }
          if (formData.details.clientReferenceNumber !== originalData.details.clientReferenceNumber) {
            updates.matter_number = formData.details.clientReferenceNumber;
          }
          if (formData.details.description !== originalData.details.description) {
            updates.description = formData.details.description;
          }
          if (formData.details.practiceAreaId !== originalData.details.practiceAreaId) {
            updates.practice_area = formData.details.practiceAreaId;
          }
          if (formData.details.jurisdictionCode !== originalData.details.jurisdictionCode) {
            updates.jurisdiction_code = formData.details.jurisdictionCode || '';
          }
          if (formData.details.caseSubtype !== originalData.details.caseSubtype) {
            updates.case_subtype = formData.details.caseSubtype || '';
          }
          if (formData.details.plaintiffPetitioner !== originalData.details.plaintiffPetitioner) {
            updates.plaintiff_petitioner = formData.details.plaintiffPetitioner || '';
          }
          if (formData.details.defendantRespondent !== originalData.details.defendantRespondent) {
            updates.defendant_respondent = formData.details.defendantRespondent || '';
          }
          if (JSON.stringify(formData.details.opposingAttorneys) !== JSON.stringify(originalData.details.opposingAttorneys)) {
            updates.opposingAttorneys = sanitizeOpposingAttorneys(formData.details.opposingAttorneys);
          }
          if (formData.details.status !== originalData.details.status) {
            const statusToStageMap: Record<string, string> = {
              'active': 'active', 'closed': 'closed', 'on_hold': 'on_hold', 'pending': 'review'
            };
            updates.stage = statusToStageMap[formData.details.status] || formData.details.status;
          }
          // --- Assignment fields (Phase C) ---
          if (formData.details.responsibleAttorneyId !== originalData.details.responsibleAttorneyId) {
            updates.responsible_attorney_id = formData.details.responsibleAttorneyId || null;
            updates.assigned_attorney_id = formData.details.responsibleAttorneyId || null;
          }
          if (formData.details.originatingAttorneyId !== originalData.details.originatingAttorneyId) {
            updates.originating_attorney_id = formData.details.originatingAttorneyId || null;
          }
          if (formData.details.responsibleStaffId !== originalData.details.responsibleStaffId) {
            updates.responsible_staff_id = formData.details.responsibleStaffId || null;
          }

          // --- Important dates + case numbers (combined for court-rules trigger wiring) ---
          {
            const importantDatesUpdates: Record<string, string | null | undefined> = {};

            // Case numbers change → derive petition_filed_date (+ removal semantics)
            const originalCaseNumbers = originalData.caseNumbers || [];
            const currentCaseNumbers = formData.caseNumbers || [];
            const caseNumbersChanged = JSON.stringify(originalCaseNumbers) !== JSON.stringify(currentCaseNumbers);

            if (caseNumbersChanged) {
              updates.caseNumbers = currentCaseNumbers;
              const newPetitionDate = extractPetitionFiledDate(currentCaseNumbers);
              const oldPetitionDate = extractPetitionFiledDate(originalCaseNumbers);
              if (newPetitionDate !== oldPetitionDate) {
                // null signals removal so changed-key detection can mark trigger_removed
                importantDatesUpdates.petition_filed_date = newPetitionDate;
              }
            }

            if (Object.keys(importantDatesUpdates).length > 0) {
              updates.important_dates = {
                ...updates.important_dates,
                ...importantDatesUpdates,
              };
            }
          }

          // --- Template field (Phase A) ---
          if (formData.template.selectedTemplateId !== originalData.template.selectedTemplateId) {
            updates.numbering_template_id = formData.template.selectedTemplateId || null;
          }

          // --- Permissions section (Phase C) ---
          if (formData.permissions.accessLevel !== originalData.permissions.accessLevel) {
            updates.access_level = formData.permissions.accessLevel;
          }
          if (JSON.stringify(formData.permissions.allowedUserIds) !== JSON.stringify(originalData.permissions.allowedUserIds)) {
            updates.allowed_user_ids = formData.permissions.allowedUserIds;
          }
          if (JSON.stringify(formData.permissions.allowedGroupIds) !== JSON.stringify(originalData.permissions.allowedGroupIds)) {
            updates.allowed_group_ids = formData.permissions.allowedGroupIds;
          }

          // --- Block users section (Phase C) ---
          if (JSON.stringify(formData.blockUsers.blockedUserIds) !== JSON.stringify(originalData.blockUsers.blockedUserIds)) {
            updates.blocked_user_ids = formData.blockUsers.blockedUserIds;
          }

          // --- Billing section — entity columns ---
          if (formData.billing.billingMethod !== originalData.billing.billingMethod) {
            updates.billing_method = formData.billing.billingMethod;
          }
          if (formData.billing.isBillable !== originalData.billing.isBillable) {
            updates.is_billable = formData.billing.isBillable;
          }
          // --- Billing entities (budget, split billing, retainer) ---
          if (formData.billing.budget !== originalData.billing.budget) {
            updates.budget = formData.billing.budget || 0;
          }
          if (JSON.stringify(formData.billing.budgetAlertThresholds) !== JSON.stringify(originalData.billing.budgetAlertThresholds)) {
            updates.budget_alert_thresholds = formData.billing.budgetAlertThresholds;
          }
          if (formData.billing.enableSplitBilling !== originalData.billing.enableSplitBilling
              || JSON.stringify(formData.billing.splitAllocationRules) !== JSON.stringify(originalData.billing.splitAllocationRules)) {
            updates.enable_split_billing = formData.billing.enableSplitBilling;
            updates.split_allocation_rules = formData.billing.splitAllocationRules;
          }
          if (JSON.stringify(formData.billing.retainerConfig) !== JSON.stringify(originalData.billing.retainerConfig)) {
            if (formData.billing.retainerConfig.enabled) {
              updates.retainer_config = {
                minimum_threshold: formData.billing.retainerConfig.minimum_threshold,
                replenish_amount: formData.billing.retainerConfig.replenish_amount,
                auto_replenish: formData.billing.retainerConfig.auto_replenish,
                notify_on_low_balance: formData.billing.retainerConfig.notify_on_low_balance,
              };
            }
          }

          // --- Notifications section (Phase D) ---
          if (JSON.stringify(formData.notifications.notificationUserIds) !== JSON.stringify(originalData.notifications.notificationUserIds)) {
            updates.notification_user_ids = formData.notifications.notificationUserIds;
          }

          // --- Related contacts ---
          const originalRelatedContacts = originalData.relatedContacts?.relatedContacts || [];
          const currentRelatedContacts = formData.relatedContacts?.relatedContacts || [];
          if (JSON.stringify(originalRelatedContacts) !== JSON.stringify(currentRelatedContacts)) {
            updates.relatedContacts = currentRelatedContacts;
          }

          // --- Custom fields ---
          if (JSON.stringify(formData.customFields.customFields) !== JSON.stringify(originalData.customFields.customFields)) {
            updates.customFieldDefinitions = formData.customFields.customFields;
          }

          // --- Document folders ---
          if (JSON.stringify(formData.documentFolders.folders) !== JSON.stringify(originalData.documentFolders.folders)) {
            updates.documentFolders = formData.documentFolders.folders;
          }

          // --- Task lists ---
          if (JSON.stringify(formData.taskLists.taskListIds) !== JSON.stringify(originalData.taskLists.taskListIds)) {
            updates.taskListIds = formData.taskLists.taskListIds;
          }

          // --- Attorney allocation (reports) ---
          if (formData.reports.originatingAttorneyAllocation !== originalData.reports.originatingAttorneyAllocation) {
            updates.originatingAttorneyAllocation = formData.reports.originatingAttorneyAllocation;
          }
          if (formData.reports.useFirmSettingsOriginating !== originalData.reports.useFirmSettingsOriginating) {
            updates.useFirmSettingsOriginating = formData.reports.useFirmSettingsOriginating;
          }
          if (formData.reports.responsibleAttorneyAllocation !== originalData.reports.responsibleAttorneyAllocation) {
            updates.responsibleAttorneyAllocation = formData.reports.responsibleAttorneyAllocation;
          }
          if (formData.reports.useFirmSettingsResponsible !== originalData.reports.useFirmSettingsResponsible) {
            updates.useFirmSettingsResponsible = formData.reports.useFirmSettingsResponsible;
          }

          // --- Litigation stage ---
          if (formData.details.stageId !== originalData.details.stageId) {
            updates.litigationStageId = formData.details.stageId;
          }

          const originalProceedings = originalData.relatedProceedings?.relatedProceedings || [];
          const currentProceedings = formData.relatedProceedings?.relatedProceedings || [];
          const relatedProceedingsChanged = JSON.stringify(originalProceedings) !== JSON.stringify(currentProceedings);
          const emailAutomationChanged = JSON.stringify(formData.documentFolders.emailAutomation) !== JSON.stringify(originalData.documentFolders.emailAutomation);

          // Check if there are any actual changes to send
          if (Object.keys(updates).length === 0 && !relatedProceedingsChanged && !emailAutomationChanged) {
            showNotice('No changes detected', 'There are no engagement changes to save.', 'info');
            return;
          }

          console.log('🔄 [MatterCreateForm] Update payload:', updates);

          // Calculate changes for user feedback
          const changes = calculateChanges(originalData, formData);
          
          // Store pending updates and show confirmation modal
          setPendingUpdates(updates);
          setChangesSummary(changes);
          setShowChangeModal(true);
          
        } else {
          // Create Mode: Create new matter
          
          // 🆕 VALIDATION: Ensure responsible staff is provided (business requirement)
          if (!formData.details.responsibleStaffId) {
            showNotice(
              'Responsible staff required',
              'Responsible staff is required for all new engagements. Please select a responsible staff member.',
              'error',
            );
            return;
          }

          // 🆕 FIX: Backend now generates matter numbers using firm configuration
          const newMatter: CreateMatterDto = {
            client_id: formData.details.clientId,
            matter_number: formData.details.clientReferenceNumber || '', // Use reference number if provided, else backend generates
            description: formData.details.description,
            practice_area: formData.details.practiceAreaId || '',
            case_subtype: formData.details.caseSubtype || undefined,
            jurisdiction_code: formData.details.jurisdictionCode || undefined,
            plaintiff_petitioner: formData.details.plaintiffPetitioner || undefined,
            defendant_respondent: formData.details.defendantRespondent || undefined,
            opposingAttorneys: sanitizeOpposingAttorneys(formData.details.opposingAttorneys),
            priority: 'medium' as const,
            caseNumbers: formData.caseNumbers,
            // Wire petition_filed_date from case numbers into important_dates
            important_dates: (() => {
              const petitionDate = extractPetitionFiledDate(formData.caseNumbers);
              if (petitionDate === null) return undefined;
              return { petition_filed_date: petitionDate };
            })(),
            relatedContacts: formData.relatedContacts.relatedContacts,
            selectedTemplateId: formData.template.useTemplate ? formData.template.selectedTemplateId : undefined,

            // Assignment fields
            responsible_attorney_id: formData.details.responsibleAttorneyId || undefined,
            originating_attorney_id: formData.details.originatingAttorneyId || undefined,
            responsible_staff_id: formData.details.responsibleStaffId, // REQUIRED

            // Permissions
            access_level: formData.permissions.accessLevel as 'everyone' | 'specific',
            allowed_user_ids: formData.permissions.allowedUserIds || [],
            allowed_group_ids: formData.permissions.allowedGroupIds || [],
            blocked_user_ids: formData.blockUsers.blockedUserIds || [],

            // Billing entity fields
            billing_method: formData.billing.billingMethod,
            is_billable: formData.billing.isBillable,
            budget: formData.billing.budget || undefined,
            budget_alert_thresholds: formData.billing.budgetAlertThresholds.length > 0 ? formData.billing.budgetAlertThresholds : undefined,
            enable_split_billing: formData.billing.enableSplitBilling,
            split_allocation_rules: formData.billing.enableSplitBilling && formData.billing.splitAllocationRules.length >= 2
              ? formData.billing.splitAllocationRules : undefined,
            retainer_config: formData.billing.retainerConfig.enabled ? {
              minimum_threshold: formData.billing.retainerConfig.minimum_threshold,
              replenish_amount: formData.billing.retainerConfig.replenish_amount,
              auto_replenish: formData.billing.retainerConfig.auto_replenish,
              notify_on_low_balance: formData.billing.retainerConfig.notify_on_low_balance,
            } : undefined,

            // Notification subscribers
            notification_user_ids: formData.notifications.notificationUserIds.length > 0
              ? formData.notifications.notificationUserIds
              : undefined,

            // Custom fields
            customFieldDefinitions: formData.customFields.customFields.length > 0
              ? formData.customFields.customFields
              : undefined,

            // Document folders
            documentFolders: formData.documentFolders.folders.length > 0
              ? formData.documentFolders.folders
              : undefined,

            // Task lists
            taskListIds: formData.taskLists.taskListIds.length > 0
              ? formData.taskLists.taskListIds
              : undefined,
            taskListNotifyAssignees: formData.taskLists.notifyAssignees,

            // Attorney allocation (reports → billing)
            originatingAttorneyAllocation: formData.reports.originatingAttorneyAllocation,
            useFirmSettingsOriginating: formData.reports.useFirmSettingsOriginating,
            responsibleAttorneyAllocation: formData.reports.responsibleAttorneyAllocation,
            useFirmSettingsResponsible: formData.reports.useFirmSettingsResponsible,

            // Additional detail fields
            litigationStageId: formData.details.stageId || undefined,
            status: formData.details.status || undefined,
          };

          // Create matter via API
          console.log('🔄 [MatterCreateForm] Creating matter with data:', newMatter);
          const createdMatter = await createMatter(newMatter);
          await syncRelatedProceedings(
            createdMatter.id,
            formData.relatedProceedings.relatedProceedings,
            []
          );
          await syncMatterEmailAutomation(createdMatter);
          
          console.log('✅ [MatterCreateForm] Created matter:', createdMatter);
          
          // Clear draft
          localStorage.removeItem('webelieve-engagement-draft');
          
          // Navigate to the newly created matter
          if (createdMatter && createdMatter.id) {
            navigate(`/engagements/${createdMatter.id}`);
          } else {
          console.error('Created engagement does not have a valid ID:', createdMatter);
            navigate('/engagements');
            showNotice(
              'Engagement created',
              'Engagement created successfully, but could not navigate to it. Please find it in the engagements list.',
              'success',
            );
          }
        }
      } catch (error) {
        console.error(`Failed to ${isEditMode ? 'update' : 'create'} matter:`, error);
        showNotice(
          `Could not ${isEditMode ? 'update' : 'create'} matter`,
          messageFromUnknownError(error, `Failed to ${isEditMode ? 'update' : 'create'} matter. Please try again.`),
          'error',
        );
      }
    }
  };

  // Render section navigation


  // Template Section
  const renderTemplateSection = () => {
    return (
    <div className="space-y-6">
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <p className="text-sm text-blue-800 dark:text-blue-200">
          Apply a numbering format to auto-generate structured matter numbers (e.g., 2026-FL-001). This does not affect task lists.
        </p>
      </div>

      <div className="space-y-4">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={formData.template.useTemplate}
            onChange={(e) => updateSection('template', { useTemplate: e.target.checked })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Use an engagement numbering format</span>
        </label>

        {formData.template.useTemplate && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Numbering format
            </label>
            <select
              value={formData.template.selectedTemplateId || ''}
              onChange={(e) => updateSection('template', { selectedTemplateId: e.target.value })}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">Select a numbering format</option>
              {formData.template.availableTemplates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.practiceArea ? `${template.practiceArea} - ` : ''}{template.name}
                </option>
              ))}
            </select>
            
            {/* Show example format when template is selected */}
            {formData.template.selectedTemplateId && (() => {
              const selectedTemplate = formData.template.availableTemplates.find(
                t => t.id === formData.template.selectedTemplateId
              );
              if (selectedTemplate?.numberingTemplate?.examples?.length) {
                return (
                  <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Example format:
                    </p>
                    <div className="space-y-1">
                      {selectedTemplate.numberingTemplate.examples.map((example, index) => (
                        <p key={index} className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                          {example}
                        </p>
                      ))}
                    </div>
                  </div>
                );
              }
              return null;
            })()}
          </div>
        )}
      </div>
    </div>
    );
  };

  // Engagement Details Section
  const renderDetailsSection = () => (
    <div className="space-y-8">
      {/* AI Conflict Check Alert */}
      {conflictCheck.hasConflicts && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-800 dark:text-amber-200">Potential Conflict Detected</h3>
              <div className="mt-2 text-sm text-amber-700 dark:text-amber-300">
                <ul className="list-disc pl-5 space-y-1">
                  {conflictCheck.conflicts.map((conflict, index) => (
                    <li key={index}>{conflict}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Suggestions */}
      {aiSuggestions.length > 0 && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
          <h3 className="text-sm font-medium text-green-800 dark:text-green-200 mb-2">AI Suggestions</h3>
          <ul className="text-sm text-green-700 dark:text-green-300 space-y-1">
            {aiSuggestions.map((suggestion, index) => (
              <li key={index} className="flex items-start">
                <span className="mr-2">-</span>
                {suggestion}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ═══════════ GROUP 1: CASE IDENTIFICATION ═══════════ */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
        <div className="px-5 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 rounded-t-lg">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Case Identification</h3>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Client */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Client <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.details.clientId}
                onChange={(e) => updateSection('details', { clientId: e.target.value })}
                className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              >
                <option value="">Select a client</option>
                {filteredContacts.map(contact => (
                  <option key={contact.id} value={contact.id}>
                    {contact.name} {contact.contact_type === 'company' ? '(Company)' : '(Individual)'}
                  </option>
                ))}
              </select>
            </div>

            {/* Matter Description */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Matter description <span className="text-red-500">*</span>
              </label>
              <LexicalEditor
                key={`description-${isEditMode ? matterId : 'create'}`}
                placeholder="Enter engagement description"
                value={formData.details.description}
                onChange={(value) => updateSection('details', { description: value })}
                className="w-full min-h-28 p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            {/* Practice Area */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Practice area
              </label>
              <select
                value={formData.details.practiceAreaId || ''}
                onChange={(e) => {
                  const newPA = e.target.value || undefined;
                  updateSection('details', {
                    practiceAreaId: newPA,
                    jurisdictionCode: undefined,
                    caseSubtype: undefined,
                  });
                  updateSection('taskLists', { taskListIds: [] });
                  setTemplatePreview(null);
                }}
                className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              >
                <option value="">Select practice area</option>
                {practiceAreas.map(area => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
            </div>

            {/* State */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                State
              </label>
              <select
                value={formData.details.stateCode || ''}
                onChange={(e) => {
                  const newState = e.target.value || undefined;
                  updateSection('details', {
                    stateCode: newState,
                    jurisdictionCode: undefined,
                    caseSubtype: undefined,
                  });
                  updateSection('taskLists', { taskListIds: [] });
                  setTemplatePreview(null);
                }}
                className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              >
                <option value="">Select a state</option>
                {US_STATES.map(s => (
                  <option key={s.code} value={s.code}>{s.name} ({s.code})</option>
                ))}
              </select>
            </div>

            {/* Court — shown when state is selected */}
            {formData.details.stateCode && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Court
                </label>
                <select
                  value={formData.details.jurisdictionCode || ''}
                  onChange={(e) => {
                    const newCode = e.target.value || undefined;
                    updateSection('details', {
                      jurisdictionCode: newCode,
                      caseSubtype: undefined,
                    });
                    updateSection('taskLists', { taskListIds: [] });
                    setTemplatePreview(null);
                  }}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                >
                  <option value="">
                    {dynamicCourts.length === 0 ? 'No courts found' : 'Select a court'}
                  </option>
                  {dynamicCourts.map(court => (
                    <option key={court.jurisdiction_code} value={court.jurisdiction_code}>
                      {court.court_name} ({court.jurisdiction_code})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Case Subtype — shown when practice area + court selected */}
            {formData.details.practiceAreaId && formData.details.jurisdictionCode && (
              <div>
                <SubtypePicker
                  practiceArea={formData.details.practiceAreaId}
                  jurisdictionCode={formData.details.jurisdictionCode}
                  value={formData.details.caseSubtype}
                  onChange={(subtypeKey, templateId) => {
                    updateSection('details', { caseSubtype: subtypeKey || undefined });
                    if (templateId) {
                      const currentIds = formData.taskLists.taskListIds;
                      if (!currentIds.includes(templateId)) {
                        updateSection('taskLists', { taskListIds: [...currentIds, templateId] });
                        // Track auto-added template for chip badge display
                        const matchedTl = availableTaskLists.find(t => t.id === templateId);
                        const subtypeLabel = subtypeKey ? subtypeKey.replace(/_/g, ' ') : 'Subtype';
                        setAutoAddedTemplateIds(prev => {
                          const next = new Map(prev);
                          next.set(templateId, {
                            subtypeLabel,
                            phaseCount: matchedTl?.total_phase_count || 0,
                            taskCount: matchedTl?.total_task_count || 0,
                          });
                          return next;
                        });
                      }
                    }
                  }}
                />
              </div>
            )}

            {/* Court Intelligence Preview */}
            {formData.details.jurisdictionCode && formData.details.practiceAreaId && (
              <div className="md:col-span-2">
                <CourtIntelligencePreview
                  jurisdictionCode={formData.details.jurisdictionCode}
                  practiceArea={formData.details.practiceAreaId}
                  stateCode={formData.details.stateCode}
                  caseSubtype={formData.details.caseSubtype}
                />
              </div>
            )}

            {/* Plaintiff / Petitioner */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Plaintiff / Petitioner
              </label>
              <input
                type="text"
                placeholder="Enter plaintiff or petitioner name"
                value={formData.details.plaintiffPetitioner || ''}
                onChange={(e) => updateSection('details', { plaintiffPetitioner: e.target.value })}
                className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>

            {/* Defendant / Respondent */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Defendant / Respondent
              </label>
              <input
                type="text"
                placeholder="Enter defendant or respondent name"
                value={formData.details.defendantRespondent || ''}
                onChange={(e) => updateSection('details', { defendantRespondent: e.target.value })}
                className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>

            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Opposing Attorney
                </label>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {formData.details.opposingAttorneys.length} / 5
                </span>
              </div>

              <div className="space-y-3">
                {formData.details.opposingAttorneys.map((attorney, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                    <div className="md:col-span-3">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">First name</label>
                      <input
                        type="text"
                        value={attorney.firstName}
                        onChange={(e) => updateOpposingAttorney(index, 'firstName', e.target.value)}
                        className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Last name</label>
                      <input
                        type="text"
                        value={attorney.lastName}
                        onChange={(e) => updateOpposingAttorney(index, 'lastName', e.target.value)}
                        className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                      />
                    </div>
                    <div className="md:col-span-5">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Email address</label>
                      <input
                        type="email"
                        value={attorney.email}
                        onChange={(e) => updateOpposingAttorney(index, 'email', e.target.value)}
                        className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                      />
                    </div>
                    <div className="md:col-span-1">
                      <button
                        type="button"
                        onClick={() => removeOpposingAttorney(index)}
                        className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-xs font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {formData.details.opposingAttorneys.length < 5 && (
                <button
                  type="button"
                  onClick={addOpposingAttorney}
                  className="mt-3 px-3 py-2 border border-blue-300 text-blue-600 hover:bg-blue-50 dark:border-blue-600 dark:text-blue-400 dark:hover:bg-blue-900/20 rounded-lg text-sm font-medium"
                >
                  Add Opposing Attorney
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════ GROUP 2: CASE NUMBERS (optional) ═══════════ */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
        <div className="px-5 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 rounded-t-lg flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
            Case Numbers
            <span className="ml-2 text-xs font-normal normal-case text-gray-400 dark:text-gray-500">optional</span>
          </h3>
          {formData.caseNumbers.length > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {formData.caseNumbers.filter(cn => cn.caseNumber.trim()).length} of {formData.caseNumbers.length} with numbers
            </span>
          )}
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Use this area for docket numbers that belong to the primary engagement. Appeals, companion cases, and cross-actions should go in the <strong>Related proceedings</strong> step instead.
            </p>
          </div>
          {formData.caseNumbers.length > 0 ? (
            <>
              {formData.caseNumbers.map((entry, index) => (
                <div key={index} className="bg-gray-50 dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                      Case #{index + 1}
                      {entry.caseNumber && (
                        <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">({entry.caseNumber})</span>
                      )}
                    </h4>
                    <button
                      onClick={() => removeCaseNumber(index)}
                      className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-xs font-medium"
                    >
                      Remove
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Case Number */}
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Case Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="e.g., 37-2024-00012345-CU-PA-NC"
                        value={entry.caseNumber}
                        onChange={(e) => updateCaseNumber(index, 'caseNumber', e.target.value)}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                      />
                    </div>

                    {/* Court — auto-filled from jurisdiction selection above */}
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Court
                      </label>
                      {dynamicCourts.length > 0 ? (
                        <select
                          value={entry.jurisdictionCode || formData.details.jurisdictionCode || ''}
                          onChange={(e) => {
                            const code = e.target.value;
                            const court = dynamicCourts.find(c => c.jurisdiction_code === code);
                            updateCaseNumber(index, 'jurisdictionCode', code);
                            updateCaseNumber(index, 'court', court?.court_name || '');
                            if (code && !entry.jurisdiction) {
                              updateCaseNumber(index, 'jurisdiction', 'State');
                            }
                          }}
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                        >
                          <option value="">Select a court</option>
                          {dynamicCourts.map(court => (
                            <option key={court.jurisdiction_code} value={court.jurisdiction_code}>
                              {court.court_name} ({court.jurisdiction_code})
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          placeholder={formData.details.stateCode ? 'No courts found for this state' : 'Select a state above to see courts'}
                          value={entry.court}
                          onChange={(e) => updateCaseNumber(index, 'court', e.target.value)}
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                        />
                      )}
                    </div>

                    {/* Jurisdiction Type */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Jurisdiction Type
                      </label>
                      <select
                        value={entry.jurisdiction}
                        onChange={(e) => updateCaseNumber(index, 'jurisdiction', e.target.value)}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                      >
                        <option value="">Select type</option>
                        <option value="State">State</option>
                        <option value="Federal">Federal</option>
                        <option value="Arbitration">Arbitration</option>
                        <option value="International">International</option>
                      </select>
                    </div>

                    {/* Case Type */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Case Type
                      </label>
                      <select
                        value={entry.caseType}
                        onChange={(e) => updateCaseNumber(index, 'caseType', e.target.value)}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                      >
                        <option value="">Select type</option>
                        <option value="Primary">Primary</option>
                        <option value="Appeal">Appeal</option>
                        <option value="Cross-Action">Cross-Action</option>
                        <option value="Related">Related</option>
                        <option value="Consolidated">Consolidated</option>
                      </select>
                    </div>

                    {/* Filed Date */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Filed Date
                      </label>
                      <input
                        type="date"
                        value={entry.filedDate || ''}
                        onChange={(e) => updateCaseNumber(index, 'filedDate', e.target.value)}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                      />
                    </div>

                    {/* Status */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Status
                      </label>
                      <select
                        value={entry.status}
                        onChange={(e) => updateCaseNumber(index, 'status', e.target.value)}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                      >
                        <option value="">Select status</option>
                        <option value="Open">Open</option>
                        <option value="Closed">Closed</option>
                        <option value="Appealed">Appealed</option>
                        <option value="Reopened">Reopened</option>
                        <option value="Stayed">Stayed</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-3">
              No case numbers added yet. Case numbers can be added after filing.
            </p>
          )}

          <button
            onClick={addCaseNumber}
            className="w-full md:w-auto px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors flex items-center justify-center"
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {formData.caseNumbers.length > 0 ? 'Add Another' : 'Add Case Number'}
          </button>
        </div>
      </div>

      {/* ═══════════ GROUP 3: ASSIGNMENTS & STATUS ═══════════ */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
        <div className="px-5 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 rounded-t-lg">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Assignments & Status</h3>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Responsible Attorney */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Responsible attorney
              </label>
              <select
                value={formData.details.responsibleAttorneyId || ''}
                onChange={(e) => updateSection('details', { responsibleAttorneyId: e.target.value })}
                className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              >
                <option value="">Find a firm user</option>
                {filteredAttorneys.map(attorney => (
                  <option key={attorney.id} value={attorney.id}>{attorney.name}</option>
                ))}
              </select>
            </div>

            {/* Originating Attorney */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Originating attorney
              </label>
              <select
                value={formData.details.originatingAttorneyId || ''}
                onChange={(e) => updateSection('details', { originatingAttorneyId: e.target.value })}
                className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              >
                <option value="">Find a firm user</option>
                {filteredAttorneys.map(attorney => (
                  <option key={attorney.id} value={attorney.id}>{attorney.name}</option>
                ))}
              </select>
            </div>

            {/* Responsible Staff */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                Responsible staff <span className="text-red-500 ml-0.5">*</span>
                <svg className="w-3.5 h-3.5 ml-1 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
              </label>
              <select
                value={formData.details.responsibleStaffId || ''}
                onChange={(e) => updateSection('details', { responsibleStaffId: e.target.value })}
                className={`w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm ${
                  !formData.details.responsibleStaffId ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                }`}
              >
                <option value="">Select responsible staff</option>
                {administrativeUsers.length > 0 && (
                  <optgroup label="Administrative Staff (Recommended)">
                    {administrativeUsers.map(attorney => (
                      <option key={attorney.id} value={attorney.id}>
                        {attorney.name} {attorney.id === suggestedResponsibleStaff?.id ? '(Auto-suggested)' : ''}
                        {attorney.roles?.some(r => r.toLowerCase().includes('admin')) && ' [Admin]'}
                        {attorney.roles?.some(r => r.toLowerCase().includes('assistant')) && ' [Assistant]'}
                        {attorney.roles?.some(r => r.toLowerCase().includes('paralegal')) && ' [Paralegal]'}
                      </option>
                    ))}
                  </optgroup>
                )}
                {filteredAttorneys.filter(attorney => !administrativeUsers.includes(attorney)).length > 0 && (
                  <optgroup label="Other Firm Users">
                    {filteredAttorneys
                      .filter(attorney => !administrativeUsers.includes(attorney))
                      .map(attorney => (
                        <option key={attorney.id} value={attorney.id}>
                          {attorney.name}
                          {attorney.roles?.some(r => r.toLowerCase().includes('attorney')) && ' [Attorney]'}
                        </option>
                      ))
                    }
                  </optgroup>
                )}
              </select>
              {!formData.details.responsibleStaffId && (
                <p className="text-xs text-red-500 dark:text-red-400 mt-1">Required for all new engagements</p>
              )}
              {formData.details.responsibleStaffId && suggestedResponsibleStaff && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Auto-suggested {suggestedResponsibleStaff.name} based on role detection.
                </p>
              )}
            </div>

            {/* Client Reference Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Client reference number
              </label>
              <input
                type="text"
                placeholder="Enter reference number"
                value={formData.details.clientReferenceNumber || ''}
                onChange={(e) => updateSection('details', { clientReferenceNumber: e.target.value })}
                className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>

            {/* Matter Stage */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Matter stage
              </label>
              <select
                value={formData.details.stageId || ''}
                onChange={(e) => updateSection('details', { stageId: e.target.value })}
                className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              >
                <option value="">Select a stage</option>
                {matterStages.map(stage => (
                  <option key={stage} value={stage}>{stage}</option>
                ))}
              </select>
            </div>

            {/* Matter Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Matter status
              </label>
              <select
                value={formData.details.status}
                onChange={(e) => updateSection('details', { status: e.target.value as MatterStatus })}
                className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              >
                <option value="active">Open</option>
                <option value="pending">Pending</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Permissions Section - 🆕 FUNCTIONAL IMPLEMENTATION
  const renderPermissionsSection = () => {
    const handleAddAllowedUser = (userId: string) => {
      if (userId && !formData.permissions.allowedUserIds.includes(userId)) {
        const newAllowedUsers = [...formData.permissions.allowedUserIds, userId];
        updateSection('permissions', { allowedUserIds: newAllowedUsers });
      }
    };

    const handleRemoveAllowedUser = (userId: string) => {
      const newAllowedUsers = formData.permissions.allowedUserIds.filter(id => id !== userId);
      updateSection('permissions', { allowedUserIds: newAllowedUsers });
    };

    const getSelectedUserNames = (userIds: string[]) => {
      return userIds.map(id => {
        const attorney = filteredAttorneys.find(a => a.id === id);
        return attorney ? attorney.name : `User ${id.slice(0, 8)}...`;
      });
    };

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            📋 Firm users with access
          </h3>
          
          {/* Access Level Selection */}
          <div className="space-y-3 mb-6">
            <label className="flex items-center">
              <input
                type="radio"
                name="accessLevel"
                value="everyone"
                checked={formData.permissions.accessLevel === 'everyone'}
                onChange={(e) => updateSection('permissions', { accessLevel: e.target.value as 'everyone' | 'specific' })}
                className="text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                🌍 Everyone (Default - all subscriber users can access this engagement)
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="accessLevel"
                value="specific"
                checked={formData.permissions.accessLevel === 'specific'}
                onChange={(e) => updateSection('permissions', { accessLevel: e.target.value as 'everyone' | 'specific' })}
                className="text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                🔒 Specific users or groups (Restrict access to selected users only)
              </span>
            </label>
          </div>

          {/* Specific User Selection - Only show when "specific" is selected */}
          {formData.permissions.accessLevel === 'specific' && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
              <h4 className="text-md font-medium text-blue-900 dark:text-blue-200 mb-3">
                Select Allowed Users
              </h4>
              
              {/* Add User Dropdown */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
                  Add user to allowed list:
                </label>
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      handleAddAllowedUser(e.target.value);
                      e.target.value = ''; // Reset selection
                    }
                  }}
                  className="w-full p-3 border border-blue-300 dark:border-blue-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-blue-900/40 dark:text-blue-100"
                >
                  <option value="">Select a user to allow access...</option>
                  {filteredAttorneys
                    .filter(attorney => !formData.permissions.allowedUserIds.includes(attorney.id))
                    .map(attorney => (
                      <option key={attorney.id} value={attorney.id}>
                        {attorney.name} ({attorney.email})
                      </option>
                    ))
                  }
                </select>
              </div>

              {/* Currently Allowed Users */}
              {formData.permissions.allowedUserIds.length > 0 && (
                <div>
                  <h5 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
                    Currently Allowed Users ({formData.permissions.allowedUserIds.length}):
                  </h5>
                  <div className="space-y-2">
                    {getSelectedUserNames(formData.permissions.allowedUserIds).map((name, index) => (
                      <div 
                        key={formData.permissions.allowedUserIds[index]} 
                        className="flex items-center justify-between bg-blue-100 dark:bg-blue-800/50 rounded-lg p-2"
                      >
                        <span className="text-sm text-blue-900 dark:text-blue-200">
                          ✅ {name}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveAllowedUser(formData.permissions.allowedUserIds[index])}
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {formData.permissions.allowedUserIds.length === 0 && (
                <div className="text-sm text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-800/30 rounded-lg p-3">
                  ⚠️ No users selected. This matter will only be accessible to you and firm administrators.
                </div>
              )}
            </div>
          )}

          {/* Information Box */}
          <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mt-4">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              📝 Permission Rules:
            </h4>
            <ul className="text-xs text-gray-700 dark:text-gray-300 space-y-1">
              <li>• <strong>Everyone:</strong> All subscriber users can access this engagement</li>
              <li>• <strong>Specific users:</strong> Only selected users + firm admins can access</li>
              <li>• <strong>Responsible Attorney/Staff:</strong> Always have access regardless of settings</li>
              <li>• <strong>Engagement Creator:</strong> Always has access to engagements they create</li>
              <li>• <strong>Blocked Users:</strong> Can be explicitly blocked in the next section</li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  // Notifications Section
  const renderNotificationsSection = () => {
    const handleAddNotificationUser = (userId: string) => {
      if (userId && !formData.notifications.notificationUserIds.includes(userId)) {
        const updated = [...formData.notifications.notificationUserIds, userId];
        updateSection('notifications', { notificationUserIds: updated });
      }
    };

    const handleRemoveNotificationUser = (userId: string) => {
      const updated = formData.notifications.notificationUserIds.filter(id => id !== userId);
      updateSection('notifications', { notificationUserIds: updated });
    };

    return (
      <div className="space-y-6">
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            Subscriber users that you select will receive notifications when the status of this engagement changes
            or the matter is deleted. They will also be notified when documents are uploaded by clients
            and related contacts.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Firm user
          </label>
          <select
            value=""
            onChange={(e) => handleAddNotificationUser(e.target.value)}
            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="">Find a firm user</option>
            {attorneys
              .filter(a => !formData.notifications.notificationUserIds.includes(a.id))
              .map(attorney => (
                <option key={attorney.id} value={attorney.id}>{attorney.name}</option>
              ))}
          </select>
        </div>

        {formData.notifications.notificationUserIds.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Selected Users ({formData.notifications.notificationUserIds.length})
            </h4>
            {formData.notifications.notificationUserIds.map(userId => {
              const user = attorneys.find(a => a.id === userId);
              return (
                <div key={userId} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-lg">
                  <span className="text-sm text-gray-900 dark:text-white">
                    {user?.name || `User ${userId.slice(0, 8)}...`}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemoveNotificationUser(userId)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    Remove
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Block Users Section - 🆕 FUNCTIONAL IMPLEMENTATION
  const renderBlockUsersSection = () => {
    const handleAddBlockedUser = (userId: string) => {
      if (userId && !formData.blockUsers.blockedUserIds.includes(userId)) {
        const newBlockedUsers = [...formData.blockUsers.blockedUserIds, userId];
        updateSection('blockUsers', { blockedUserIds: newBlockedUsers });
      }
    };

    const handleRemoveBlockedUser = (userId: string) => {
      const newBlockedUsers = formData.blockUsers.blockedUserIds.filter(id => id !== userId);
      updateSection('blockUsers', { blockedUserIds: newBlockedUsers });
    };

    const getBlockedUserNames = (userIds: string[]) => {
      return userIds.map(id => {
        const attorney = filteredAttorneys.find(a => a.id === id);
        return attorney ? attorney.name : `User ${id.slice(0, 8)}...`;
      });
    };

    return (
      <div className="space-y-6">
        {/* Warning Box */}
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
          <h4 className="text-md font-medium text-amber-900 dark:text-amber-200 mb-2">
            🚫 Block Users from Matter Access
          </h4>
          <p className="text-sm text-amber-800 dark:text-amber-200">
            Prevent users from accessing this engagement by blocking them. Blocking will override any engagement
            permissions, including any groups that the users are a part of. Only admins can block a user.
          </p>
        </div>
        
        {/* Add Blocked User */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Choose users to block
          </label>
          <select 
            onChange={(e) => {
              if (e.target.value) {
                handleAddBlockedUser(e.target.value);
                e.target.value = ''; // Reset selection
              }
            }}
            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="">Select a user to block...</option>
            {filteredAttorneys
              .filter(attorney => 
                !formData.blockUsers.blockedUserIds.includes(attorney.id) &&
                attorney.id !== formData.details.responsibleAttorneyId &&
                attorney.id !== formData.details.originatingAttorneyId &&
                attorney.id !== formData.details.responsibleStaffId
              )
              .map(attorney => (
                <option key={attorney.id} value={attorney.id}>
                  {attorney.name} ({attorney.email})
                </option>
              ))
            }
          </select>
          
          {/* Note about not being able to block assigned users */}
          {(formData.details.responsibleAttorneyId || formData.details.originatingAttorneyId || formData.details.responsibleStaffId) && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
              💡 Note: Assigned attorneys and responsible staff cannot be blocked
            </p>
          )}
        </div>

        {/* Currently Blocked Users */}
        {formData.blockUsers.blockedUserIds.length > 0 && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
            <h4 className="text-md font-medium text-red-900 dark:text-red-200 mb-3">
              Blocked Users ({formData.blockUsers.blockedUserIds.length})
            </h4>
            <div className="space-y-2">
              {getBlockedUserNames(formData.blockUsers.blockedUserIds).map((name, index) => (
                <div 
                  key={formData.blockUsers.blockedUserIds[index]} 
                  className="flex items-center justify-between bg-red-100 dark:bg-red-800/50 rounded-lg p-2"
                >
                  <span className="text-sm text-red-900 dark:text-red-200">
                    🚫 {name}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemoveBlockedUser(formData.blockUsers.blockedUserIds[index])}
                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm"
                  >
                    Unblock
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {formData.blockUsers.blockedUserIds.length === 0 && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-3">
            <p className="text-sm text-green-800 dark:text-green-200">
              ✅ No users are blocked. All permitted users can access this engagement.
            </p>
          </div>
        )}

        {/* Information Box */}
        <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
            🛡️ Blocking Rules:
          </h4>
          <ul className="text-xs text-gray-700 dark:text-gray-300 space-y-1">
            <li>• <strong>Override All Permissions:</strong> Blocked users cannot access regardless of other settings</li>
            <li>• <strong>Admin Only:</strong> Only firm administrators can block users</li>
            <li>• <strong>Cannot Block Assigned:</strong> Responsible attorney/staff cannot be blocked</li>
            <li>• <strong>Search Prevention:</strong> Blocked users cannot find this engagement in searches</li>
          </ul>
        </div>
      </div>
    );
  };

  const renderRelatedProceedingsSection = () => {
    const addRelatedProceeding = () => {
      updateSection('relatedProceedings', {
        relatedProceedings: [
          ...formData.relatedProceedings.relatedProceedings,
          defaultRelatedProceedingDraft(formData.details.jurisdictionCode, formData.details.practiceAreaId)
        ]
      });
    };

    const removeRelatedProceeding = (index: number) => {
      updateSection('relatedProceedings', {
        relatedProceedings: formData.relatedProceedings.relatedProceedings.filter((_, i) => i !== index)
      });
    };

    const updateRelatedProceeding = (
      index: number,
      field: keyof RelatedProceedingDraft,
      value: string | boolean
    ) => {
      const updated = [...formData.relatedProceedings.relatedProceedings];
      updated[index] = { ...updated[index], [field]: value };
      updateSection('relatedProceedings', { relatedProceedings: updated });
    };

    return (
      <div className="space-y-6">
        <div className="bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-700 rounded-lg p-4">
          <p className="text-sm text-cyan-800 dark:text-cyan-200">
            Use related proceedings for appeals, enforcement actions, companion cases, parallel civil actions, or anything that belongs to the same dispute ecosystem but should not become the primary engagement right away.
          </p>
        </div>

        {formData.relatedProceedings.relatedProceedings.length > 0 ? (
          <div className="space-y-4">
            {formData.relatedProceedings.relatedProceedings.map((entry, index) => (
              <div key={entry.proceeding_id || `draft-${index}`} className="bg-white dark:bg-gray-800 p-6 border border-gray-200 dark:border-gray-700 rounded-lg space-y-4">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                    Related Proceeding {index + 1}
                  </h4>
                  <button
                    onClick={() => removeRelatedProceeding(index)}
                    className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Proceeding title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={entry.title}
                      onChange={(e) => updateRelatedProceeding(index, 'title', e.target.value)}
                      placeholder="Nevada Probate Action"
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Relationship type
                    </label>
                    <select
                      value={entry.relationship_type}
                      onChange={(e) => updateRelatedProceeding(index, 'relationship_type', e.target.value)}
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="companion_case">Companion case</option>
                      <option value="appeal_of">Appeal of</option>
                      <option value="enforcement_of">Enforcement of</option>
                      <option value="related_family_case">Related family case</option>
                      <option value="parallel_civil_action">Parallel civil action</option>
                      <option value="probate_dependency">Probate dependency</option>
                      <option value="administrative_action">Administrative action</option>
                      <option value="subsequent_action">Subsequent action</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Practice area
                    </label>
                    <input
                      type="text"
                      value={entry.practice_area || ''}
                      onChange={(e) => updateRelatedProceeding(index, 'practice_area', e.target.value)}
                      placeholder="Probate"
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Jurisdiction code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={entry.jurisdiction_code}
                      onChange={(e) => updateRelatedProceeding(index, 'jurisdiction_code', e.target.value)}
                      placeholder="NV-32003"
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Court name
                    </label>
                    <input
                      type="text"
                      value={entry.court_name || ''}
                      onChange={(e) => updateRelatedProceeding(index, 'court_name', e.target.value)}
                      placeholder="Clark County Probate Court"
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Case number
                    </label>
                    <input
                      type="text"
                      value={entry.case_number || ''}
                      onChange={(e) => updateRelatedProceeding(index, 'case_number', e.target.value)}
                      placeholder="PR-2026-001245"
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Filing date
                    </label>
                    <input
                      type="date"
                      value={entry.filing_date || ''}
                      onChange={(e) => updateRelatedProceeding(index, 'filing_date', e.target.value)}
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Status
                    </label>
                    <select
                      value={entry.status}
                      onChange={(e) => updateRelatedProceeding(index, 'status', e.target.value)}
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="planned">Planned</option>
                      <option value="open">Open</option>
                      <option value="pending">Pending</option>
                      <option value="stayed">Stayed</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Strategic importance
                    </label>
                    <select
                      value={entry.strategic_importance}
                      onChange={(e) => updateRelatedProceeding(index, 'strategic_importance', e.target.value)}
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Summary
                    </label>
                    <textarea
                      value={entry.summary || ''}
                      onChange={(e) => updateRelatedProceeding(index, 'summary', e.target.value)}
                      placeholder="How this proceeding affects the primary engagement..."
                      rows={3}
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div className="md:col-span-2 flex flex-wrap gap-6">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={entry.affects_deadlines}
                        onChange={(e) => updateRelatedProceeding(index, 'affects_deadlines', e.target.checked)}
                        className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Affects deadlines</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={entry.affects_conflicts}
                        onChange={(e) => updateRelatedProceeding(index, 'affects_conflicts', e.target.checked)}
                        className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Affects conflicts</span>
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-gray-50 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
            <div className="text-gray-400 dark:text-gray-500">
              <svg className="mx-auto h-12 w-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-lg font-medium mb-2">No related proceedings added</p>
              <p className="text-sm">Add appeals, companion cases, and cross-jurisdiction proceedings here.</p>
            </div>
          </div>
        )}

        <button
          onClick={addRelatedProceeding}
          className="text-cyan-600 hover:text-cyan-800 dark:text-cyan-400 dark:hover:text-cyan-300 text-sm font-medium flex items-center"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {formData.relatedProceedings.relatedProceedings.length > 0 ? 'Add Another Proceeding' : 'Add Related Proceeding'}
        </button>
      </div>
    );
  };

  // Related Contacts Section
  const renderRelatedContactsSection = () => {
    const addRelatedContact = () => {
      const newContact: RelatedContact = {
        contactId: '',
        relationship: '',
        isBillRecipient: false
      };
      updateSection('relatedContacts', {
        relatedContacts: [...formData.relatedContacts.relatedContacts, newContact]
      });
    };

    const removeRelatedContact = (index: number) => {
      const updatedContacts = formData.relatedContacts.relatedContacts.filter((_, i) => i !== index);
      updateSection('relatedContacts', { relatedContacts: updatedContacts });
    };

    const updateRelatedContact = (index: number, field: keyof RelatedContact, value: string | boolean) => {
      const updatedContacts = [...formData.relatedContacts.relatedContacts];
      updatedContacts[index] = { ...updatedContacts[index], [field]: value };
      updateSection('relatedContacts', { relatedContacts: updatedContacts });
    };

    return (
      <div className="space-y-6">
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            Information for related contacts appears on the matter dashboard. These contacts will also 
            appear in future conflict checks.
          </p>
        </div>
        
        {formData.relatedContacts.relatedContacts.length > 0 ? (
          <div className="space-y-4">
            {formData.relatedContacts.relatedContacts.map((contact, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 p-6 border border-gray-200 dark:border-gray-700 rounded-lg space-y-4">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                    Related Contact {index + 1}
                  </h4>
                  <button 
                    onClick={() => removeRelatedContact(index)} 
                    className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Contact
                    </label>
                    <select 
                      value={contact.contactId}
                      onChange={(e) => updateRelatedContact(index, 'contactId', e.target.value)}
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">Select a contact</option>
                      {filteredContacts.map(contactOption => (
                        <option key={contactOption.id} value={contactOption.id}>
                          {contactOption.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Relationship
                    </label>
                    <input
                      type="text"
                      value={contact.relationship}
                      onChange={(e) => updateRelatedContact(index, 'relationship', e.target.value)}
                      placeholder="e.g., Spouse, Business Partner"
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  
                  <div className="flex items-end">
                    <label className="flex items-center">
                      <input 
                        type="checkbox" 
                        checked={contact.isBillRecipient}
                        onChange={(e) => updateRelatedContact(index, 'isBillRecipient', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" 
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Bill recipient</span>
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-gray-50 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
            <div className="text-gray-400 dark:text-gray-500">
              <svg className="mx-auto h-12 w-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-lg font-medium mb-2">No related contacts added</p>
              <p className="text-sm">Add related contacts to track relationships and billing information.</p>
            </div>
          </div>
        )}
        
        <button 
          onClick={addRelatedContact}
          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium flex items-center"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {formData.relatedContacts.relatedContacts.length > 0 ? 'Add Another Related Contact' : 'Add Related Contact'}
        </button>
      </div>
    );
  };

  // Custom Fields Section
  const renderCustomFieldsSection = () => {
    const handleAddCustomField = () => {
      const newField: CustomField = {
        id: `cf-${Date.now()}`,
        name: '',
        type: 'text',
        value: '',
        required: false
      };
      updateSection('customFields', {
        customFields: [...formData.customFields.customFields, newField]
      });
    };

    const handleUpdateCustomField = (fieldId: string, updates: Partial<CustomField>) => {
      const updated = formData.customFields.customFields.map(f =>
        f.id === fieldId ? { ...f, ...updates } : f
      );
      updateSection('customFields', { customFields: updated });
    };

    const handleRemoveCustomField = (fieldId: string) => {
      const updated = formData.customFields.customFields.filter(f => f.id !== fieldId);
      updateSection('customFields', { customFields: updated });
    };

    return (
      <div className="space-y-6">
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            Speed up your workflow by creating custom field sets for often-used custom fields.
            Customize data within your template to always be visible for a Matter.
          </p>
        </div>

        {formData.customFields.customFields.map((field) => (
          <div key={field.id} className="flex items-start gap-3 bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
              <input
                type="text"
                placeholder="Field name"
                value={field.name}
                onChange={(e) => handleUpdateCustomField(field.id, { name: e.target.value })}
                className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
              />
              <select
                value={field.type}
                onChange={(e) => handleUpdateCustomField(field.id, { type: e.target.value as CustomField['type'] })}
                className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
              >
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="date">Date</option>
                <option value="dropdown">Dropdown</option>
                <option value="checkbox">Checkbox</option>
              </select>
              <input
                type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                placeholder="Default value"
                value={String(field.value || '')}
                onChange={(e) => handleUpdateCustomField(field.id, { value: e.target.value })}
                className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
              />
            </div>
            <button
              type="button"
              onClick={() => handleRemoveCustomField(field.id)}
              className="text-red-500 hover:text-red-700 mt-2 text-sm"
            >
              Remove
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={handleAddCustomField}
          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium"
        >
          + Add a custom field or custom field set
        </button>
      </div>
    );
  };

  // Billing Section
  const renderBillingSection = () => (
    <div className="space-y-6">
      <div>
        <label className="flex items-center mb-4">
          <input
            type="checkbox"
            checked={formData.billing.isBillable}
            onChange={(e) => updateSection('billing', { isBillable: e.target.checked })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            This matter is billable
          </span>
          <InfoTooltip content="Configure billing method, rates, and retainer requirements for this engagement." />
        </label>
      </div>

      {formData.billing.isBillable && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Billing method
            </label>
            <select
              value={formData.billing.billingMethod}
              onChange={(e) => updateSection('billing', { billingMethod: e.target.value })}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="Hourly">Hourly</option>
              <option value="Flat">Fixed Fee</option>
              <option value="Contingency">Contingency</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Matter budget
            </label>
            <input
              type="number"
              placeholder="Set a budget for this engagement"
              value={formData.billing.budget || ''}
              onChange={(e) => updateSection('billing', { budget: e.target.value ? Number(e.target.value) : undefined })}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Split Billing */}
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.billing.enableSplitBilling}
                onChange={(e) => updateSection('billing', { enableSplitBilling: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Enable split billing for this engagement
              </span>
            </label>

            {formData.billing.enableSplitBilling && (
              <div className="ml-6 space-y-3 border-l-2 border-blue-200 pl-4">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Allocate billing across multiple parties. Percentages must sum to 100% with at least 2 parties.
                </p>
                {formData.billing.splitAllocationRules.map((rule, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Client name"
                      value={rule.client_name}
                      onChange={(e) => {
                        const updated = [...formData.billing.splitAllocationRules];
                        updated[idx] = { ...updated[idx], client_name: e.target.value };
                        updateSection('billing', { splitAllocationRules: updated });
                      }}
                      className="flex-1 p-2 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white"
                    />
                    <input
                      type="number"
                      placeholder="%"
                      min="0"
                      max="100"
                      value={rule.percentage || ''}
                      onChange={(e) => {
                        const updated = [...formData.billing.splitAllocationRules];
                        updated[idx] = { ...updated[idx], percentage: Number(e.target.value) };
                        updateSection('billing', { splitAllocationRules: updated });
                      }}
                      className="w-20 p-2 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white"
                    />
                    <span className="text-sm text-gray-500">%</span>
                    <button
                      type="button"
                      onClick={() => {
                        const updated = formData.billing.splitAllocationRules.filter((_, i) => i !== idx);
                        updateSection('billing', { splitAllocationRules: updated });
                      }}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    updateSection('billing', {
                      splitAllocationRules: [
                        ...formData.billing.splitAllocationRules,
                        { client_id: '', client_name: '', percentage: 0 },
                      ],
                    });
                  }}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  + Add party
                </button>
                {formData.billing.splitAllocationRules.length > 0 && (
                  <p className={`text-xs ${
                    formData.billing.splitAllocationRules.reduce((s, r) => s + r.percentage, 0) === 100
                      ? 'text-green-600' : 'text-red-600'
                  }`}>
                    Total: {formData.billing.splitAllocationRules.reduce((s, r) => s + r.percentage, 0)}%
                    {formData.billing.splitAllocationRules.reduce((s, r) => s + r.percentage, 0) !== 100 && ' (must be 100%)'}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Retainer / Trust Management */}
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.billing.retainerConfig.enabled}
                onChange={(e) => updateSection('billing', {
                  retainerConfig: { ...formData.billing.retainerConfig, enabled: e.target.checked }
                })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Enable retainer / trust management
              </span>
            </label>

            {formData.billing.retainerConfig.enabled && (
              <div className="ml-6 space-y-3 border-l-2 border-blue-200 pl-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Low balance threshold ($)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.billing.retainerConfig.minimum_threshold || ''}
                      onChange={(e) => updateSection('billing', {
                        retainerConfig: { ...formData.billing.retainerConfig, minimum_threshold: Number(e.target.value) }
                      })}
                      className="w-full p-2 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Replenish amount ($)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.billing.retainerConfig.replenish_amount || ''}
                      onChange={(e) => updateSection('billing', {
                        retainerConfig: { ...formData.billing.retainerConfig, replenish_amount: Number(e.target.value) }
                      })}
                      className="w-full p-2 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.billing.retainerConfig.auto_replenish}
                    onChange={(e) => updateSection('billing', {
                      retainerConfig: { ...formData.billing.retainerConfig, auto_replenish: e.target.checked }
                    })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-xs text-gray-700 dark:text-gray-300">
                    Auto-replenish when balance drops below threshold
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.billing.retainerConfig.notify_on_low_balance}
                    onChange={(e) => updateSection('billing', {
                      retainerConfig: { ...formData.billing.retainerConfig, notify_on_low_balance: e.target.checked }
                    })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-xs text-gray-700 dark:text-gray-300">
                    Notify when balance is low
                  </span>
                </label>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // (Case Numbers section merged into renderDetailsSection above)

  // Task Lists: smart filtering, ranking, expanded state
  const [expandedTaskListIds, setExpandedTaskListIds] = useState<Set<string>>(new Set());

  // Enhancement 1: Smart filtering & ranking (derived from matter context)
  const filteredAndRankedTaskLists = useMemo(() => {
    const matterJurisdiction = formData.details.jurisdictionCode || null;
    const matterState = formData.details.stateCode || null;
    const matterSubtype = formData.details.caseSubtype || null;

    // Step 1: Remove drafts (only show validated/stale)
    let filtered = availableTaskLists.filter(tl => tl.lifecycle_status !== 'draft');

    // Step 2: When a firm_variant exists for a parent, hide the parent
    const firmVariantParentIds = new Set(
      filtered
        .filter(tl => tl.template_kind === 'firm_variant' && tl.parent_template_id)
        .map(tl => tl.parent_template_id!)
    );
    filtered = filtered.filter(tl => !firmVariantParentIds.has(tl.id));

    // Step 3: Score by relevance to matter context
    const scored = filtered.map(tl => {
      let score = 0;
      // Exact jurisdiction match (e.g., CA-06073)
      if (matterJurisdiction && tl.jurisdiction_code === matterJurisdiction) score += 100;
      // Same state match (e.g., jurisdiction starts with CA)
      else if (matterState && tl.jurisdiction_code?.startsWith(matterState)) score += 50;
      // Has jurisdiction at all vs generic
      else if (tl.jurisdiction_code) score += 10;
      // Case subtype match
      if (matterSubtype && tl.case_subtype === matterSubtype) score += 80;
      else if (matterSubtype && tl.case_subtype) score -= 20; // Different subtype, penalize
      // Prefer firm_variant over base/jurisdiction_base
      if (tl.template_kind === 'firm_variant') score += 5;
      // Prefer templates with more content
      if ((tl.total_task_count || 0) > 0) score += 3;
      return { ...tl, _score: score };
    });

    // Sort: highest score first, then by name
    scored.sort((a, b) => b._score - a._score || a.name.localeCompare(b.name));
    return scored;
  }, [availableTaskLists, formData.details.jurisdictionCode, formData.details.stateCode, formData.details.caseSubtype]);

  // Recommended template = top scored if score > 0
  const recommendedTaskListId = filteredAndRankedTaskLists.length > 0 && filteredAndRankedTaskLists[0]._score > 0
    ? filteredAndRankedTaskLists[0].id : null;

  // Task Lists Section renderer
  const renderTaskListsSection = () => {
    const handleAddTaskList = (taskListId: string) => {
      if (taskListId && !formData.taskLists.taskListIds.includes(taskListId)) {
        updateSection('taskLists', {
          taskListIds: [...formData.taskLists.taskListIds, taskListId]
        });
      }
    };

    const handleRemoveTaskList = (taskListId: string) => {
      updateSection('taskLists', {
        taskListIds: formData.taskLists.taskListIds.filter(id => id !== taskListId)
      });
      setExpandedTaskListIds(prev => { const next = new Set(prev); next.delete(taskListId); return next; });
      setAutoAddedTemplateIds(prev => { const next = new Map(prev); next.delete(taskListId); return next; });
    };

    const toggleExpanded = (id: string) => {
      setExpandedTaskListIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
      });
    };

    const filteredAndRanked = filteredAndRankedTaskLists;
    const recommendedId = recommendedTaskListId;
    const currentJurisdiction = formData.details.jurisdictionCode || null;
    const currentState = formData.details.stateCode || null;

    // --- Enhancement 2: Badge helpers ---
    const renderBadge = (label: string, color: string) => (
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${color} ml-1`}>
        {label}
      </span>
    );

    const renderTemplateMeta = (tl: typeof filteredAndRanked[0], compact = false) => {
      const badges: React.ReactNode[] = [];
      if (tl.id === recommendedId) {
        badges.push(renderBadge('Recommended', 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'));
      }
      if (tl.template_kind === 'firm_variant') {
        badges.push(renderBadge('Firm Custom', 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300'));
      }
      if (currentJurisdiction && tl.jurisdiction_code === currentJurisdiction) {
        badges.push(renderBadge('Jurisdiction Match', 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'));
      } else if (currentState && tl.jurisdiction_code?.startsWith(currentState)) {
        badges.push(renderBadge('Same State', 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300'));
      } else if (!tl.jurisdiction_code) {
        badges.push(renderBadge('Generic', 'bg-gray-100 text-gray-600 dark:bg-gray-600 dark:text-gray-300'));
      }
      if (tl.case_subtype) {
        // Letter-for-letter fidelity: show the jurisdiction's local label
        // when the backend resolver supplied one; fall back to the canonical
        // key only if no local term is configured for this jurisdiction.
        const subtypeBadgeText = tl.case_subtype_local_label || tl.case_subtype.replace(/_/g, ' ');
        badges.push(renderBadge(subtypeBadgeText, 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'));
      }
      // Lifecycle trust badge
      if (tl.lifecycle_status === 'validated') {
        badges.push(renderBadge('Validated', 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'));
      } else if (tl.lifecycle_status === 'stale') {
        badges.push(renderBadge('Stale', 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'));
      }
      // Confidence indicator
      if (tl.confidence_score) {
        const dotColor = tl.confidence_score === 'high'
          ? 'bg-green-500' : tl.confidence_score === 'medium'
          ? 'bg-amber-500' : 'bg-red-500';
        badges.push(
          <span key="confidence" className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 dark:text-gray-400 ml-0.5">
            <span className={`inline-block h-2 w-2 rounded-full ${dotColor}`} />
            {tl.confidence_score}
          </span>
        );
      }

      const stats = compact ? null : (
        <span className="text-[11px] text-gray-400 dark:text-gray-500 ml-2">
          {tl.total_task_count || 0} tasks · {tl.total_phase_count || 0} phases · {tl.total_form_count || 0} forms
        </span>
      );

      return (
        <span className="inline-flex items-center flex-wrap gap-0.5">
          {badges}
          {stats}
        </span>
      );
    };

    // --- Enhancement 3: Expandable phase preview ---
    const renderPhasePreview = (tl: typeof filteredAndRanked[0]) => {
      if (!tl.phases || tl.phases.length === 0) {
        return (
          <p className="text-xs text-gray-400 dark:text-gray-500 italic">No phase details available</p>
        );
      }
      return (
        <div className="space-y-2">
          {tl.phases.sort((a, b) => a.order - b.order).map((phase, idx) => (
            <div key={idx} className="border-l-2 border-blue-400 dark:border-blue-600 pl-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-800 dark:text-gray-200">
                  {phase.order}. {phase.name}
                </span>
                <span className="text-[10px] text-gray-400 dark:text-gray-500">
                  {phase.task_count} task{phase.task_count !== 1 ? 's' : ''}
                </span>
              </div>
              {phase.form_numbers.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {phase.form_numbers.map((fn, fi) => (
                    <span key={fi} className="inline-block px-1.5 py-0.5 text-[10px] bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded">
                      {fn}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="flex gap-4 text-[11px] text-gray-500 dark:text-gray-400 pt-1 border-t border-gray-200 dark:border-gray-600">
            <span>Total: {tl.total_task_count || 0} tasks</span>
            <span>{tl.total_form_count || 0} unique forms</span>
            <span>{tl.total_phase_count || 0} phases</span>
          </div>
        </div>
      );
    };

    return (
      <div className="space-y-6">
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            Automatically generate and assign tasks when this engagement is created.
            <InfoTooltip content="Task lists create tasks with projected deadlines, required court forms, and automated reminders when this engagement opens." />
          </p>
        </div>

        {/* Court Intelligence Snapshot (Sprint 4H) */}
        {courtSnapshot && formData.taskLists.taskListIds.length > 0 && (
          <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-lg p-4">
            {courtSnapshot.courtName && (
              <p className="text-xs font-medium text-indigo-700 dark:text-indigo-300 mb-2">{courtSnapshot.courtName}</p>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center">
                <div className="text-lg font-bold text-gray-900 dark:text-white">{courtSnapshot.feeCount}</div>
                <div className="text-[10px] text-gray-500 dark:text-gray-400">Fee Items</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-gray-900 dark:text-white">{courtSnapshot.judgeCount}</div>
                <div className="text-[10px] text-gray-500 dark:text-gray-400">Judges</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-gray-900 dark:text-white">{courtSnapshot.mandatedDeadlines}</div>
                <div className="text-[10px] text-gray-500 dark:text-gray-400">Court Deadlines</div>
              </div>
            </div>
          </div>
        )}

        <div>
          <label className="flex items-center mb-4">
            <input
              type="checkbox"
              checked={formData.taskLists.notifyAssignees}
              onChange={(e) => updateSection('taskLists', { notifyAssignees: e.target.checked })}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Notify assignees when these tasks are created
            </span>
          </label>
        </div>

        {/* Task list dropdown — smart filtered and ranked */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Task list
          </label>
          <select
            value=""
            onChange={(e) => handleAddTaskList(e.target.value)}
            disabled={!formData.details.practiceAreaId}
            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">
              {!formData.details.practiceAreaId
                ? 'Select a practice area first'
                : filteredAndRanked.filter(tl => !formData.taskLists.taskListIds.includes(tl.id)).length === 0
                  ? 'No task lists available'
                  : 'Find a task list'}
            </option>
            {filteredAndRanked
              .filter(tl => !formData.taskLists.taskListIds.includes(tl.id))
              .map(tl => (
                <option key={tl.id} value={tl.id}>
                  {tl.id === recommendedId ? '\u2605 ' : ''}{tl.name}
                  {' '}({tl.total_task_count || 0} tasks, {tl.total_form_count || 0} forms)
                  {tl.id === recommendedId ? ' — Recommended' : ''}
                </option>
              ))}
          </select>
          {filteredAndRanked.length > 0 && filteredAndRanked.length < availableTaskLists.length && (
            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
              Showing {filteredAndRanked.length} of {availableTaskLists.length} templates (draft and superseded templates hidden)
            </p>
          )}
          {/* Compare templates link (Sprint 4I) */}
          {filteredAndRanked.filter(tl => !formData.taskLists.taskListIds.includes(tl.id)).length >= 2 && (
            <button
              type="button"
              onClick={() => setShowComparisonModal(true)}
              className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 mt-1 transition-colors"
            >
              Compare templates side-by-side
            </button>
          )}
        </div>

        {/* Auto-added template chip badges (Sprint 3E) */}
        {autoAddedTemplateIds.size > 0 && (
          <div className="flex flex-wrap gap-2">
            {Array.from(autoAddedTemplateIds.entries()).map(([id, meta]) => (
              <span key={id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-700">
                Auto-added: {meta.subtypeLabel} ({meta.phaseCount} phases, {meta.taskCount} tasks)
                <button
                  type="button"
                  onClick={() => {
                    handleRemoveTaskList(id);
                    setAutoAddedTemplateIds(prev => { const next = new Map(prev); next.delete(id); return next; });
                  }}
                  className="ml-0.5 hover:text-indigo-600 dark:hover:text-indigo-200"
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Duplicate template detection warning (Sprint 3F) */}
        {(() => {
          const selectedTls = formData.taskLists.taskListIds
            .map(id => filteredAndRanked.find(t => t.id === id) || availableTaskLists.find(t => t.id === id))
            .filter(Boolean) as typeof availableTaskLists;
          const warnings: string[] = [];
          for (let i = 0; i < selectedTls.length; i++) {
            for (let j = i + 1; j < selectedTls.length; j++) {
              const a = selectedTls[i], b = selectedTls[j];
              if (a.template_family_key && b.template_family_key && a.template_family_key === b.template_family_key) {
                warnings.push(`"${a.name}" and "${b.name}" may create duplicate tasks (same template family).`);
              } else if (a.case_subtype && b.case_subtype && a.case_subtype === b.case_subtype) {
                warnings.push(`"${a.name}" and "${b.name}" may create duplicate tasks (same case subtype).`);
              }
            }
          }
          if (warnings.length === 0) return null;
          return (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
              <div className="flex gap-2">
                <svg className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" /></svg>
                <div className="space-y-1">
                  {warnings.map((w, idx) => (
                    <p key={idx} className="text-sm text-amber-800 dark:text-amber-300">{w}</p>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Selected task lists — with metadata badges and expandable preview */}
        {formData.taskLists.taskListIds.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Selected Task Lists ({formData.taskLists.taskListIds.length})
            </h4>
            {formData.taskLists.taskListIds.map(taskListId => {
              const tl = filteredAndRanked.find(t => t.id === taskListId)
                || availableTaskLists.find(t => t.id === taskListId);
              const isExpanded = expandedTaskListIds.has(taskListId);

              return (
                <div key={taskListId} className="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
                  {/* Header row */}
                  <div className="flex items-center justify-between px-3 py-2.5">
                    <div className="flex-1 min-w-0">
                      <button
                        type="button"
                        onClick={() => tl && toggleExpanded(taskListId)}
                        className="flex items-center gap-1 text-sm font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-left"
                      >
                        <span className={`text-[10px] transition-transform ${isExpanded ? 'rotate-90' : ''}`}>&#9654;</span>
                        <span className="truncate">{tl?.name || taskListId}</span>
                      </button>
                      {tl && (
                        <div className="mt-1">
                          {renderTemplateMeta(tl as typeof filteredAndRanked[0])}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-3 shrink-0">
                      <button
                        type="button"
                        onClick={() => setPreviewPanelTemplateId(taskListId)}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm transition-colors"
                      >
                        Preview
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveTaskList(taskListId)}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  {/* Expandable preview panel */}
                  {isExpanded && tl && (
                    <div className="px-4 pb-3 pt-1 border-t border-gray-200 dark:border-gray-600 bg-gray-100/50 dark:bg-gray-800/50">
                      {renderPhasePreview(tl as typeof filteredAndRanked[0])}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Document Folders Section
  const renderDocumentFoldersSection = () => {
    const handleAddFolder = () => {
      if (newFolderName.trim()) {
        const folder: DocumentFolder = {
          name: newFolderName.trim(),
          categoryId: newFolderCategory || 'general'
        };
        updateSection('documentFolders', {
          folders: [...formData.documentFolders.folders, folder]
        });
        setNewFolderName('');
        setNewFolderCategory('');
      }
    };

    const handleRemoveFolder = (index: number) => {
      const updated = formData.documentFolders.folders.filter((_, i) => i !== index);
      updateSection('documentFolders', { folders: updated });
    };

    const emailAutomation = formData.documentFolders.emailAutomation;
    const updateEmailAutomation = (patch: Partial<MatterEmailAutomationSection>) => {
      updateSection('documentFolders', {
        emailAutomation: {
          ...emailAutomation,
          ...patch,
        },
      });
      setEmailAutomationPreview(null);
      setEmailAutomationStatus(null);
    };

    const addParticipant = (participant: MatterEmailAutomationParticipant) => {
      const email = participant.email.trim().toLowerCase();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
      if (emailAutomation.participants.some((existing) => existing.email.toLowerCase() === email)) return;
      updateEmailAutomation({
        participants: [
          ...emailAutomation.participants,
          {
            name: participant.name?.trim() || null,
            email,
            role: participant.role?.trim() || null,
          },
        ],
      });
    };

    const handleAddParticipant = () => {
      addParticipant({ name: newParticipantName, email: newParticipantEmail });
      setNewParticipantName('');
      setNewParticipantEmail('');
    };

    const relatedContactSuggestions = formData.relatedContacts.relatedContacts
      .map((related) => contacts.find((contact) => contact.id === related.contactId))
      .filter((contact): contact is NonNullable<typeof contact> => Boolean(contact?.email))
      .filter((contact) => !emailAutomation.participants.some((participant) => participant.email.toLowerCase() === contact.email!.toLowerCase()))
      .slice(0, 8);

    return (
      <div className="space-y-6">
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            Create folders within this engagement to keep incoming files organized.
            <InfoTooltip content="Document folders organize uploaded files by category. Folders are created when the engagement opens." />
          </p>
        </div>

        {formData.documentFolders.folders.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Document Folders ({formData.documentFolders.folders.length})
            </h4>
            {formData.documentFolders.folders.map((folder, index) => (
              <div key={index} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-lg">
                <div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{folder.name}</span>
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">({folder.categoryId})</span>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveFolder(index)}
                  className="text-red-500 hover:text-red-700 text-sm"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Folder name
            </label>
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Enter folder name"
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Category
            </label>
            <select
              value={newFolderCategory}
              onChange={(e) => setNewFolderCategory(e.target.value)}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">Find a document category</option>
              <option value="correspondence">Correspondence</option>
              <option value="pleadings">Pleadings</option>
              <option value="discovery">Discovery</option>
              <option value="contracts">Contracts</option>
              <option value="evidence">Evidence</option>
              <option value="general">General</option>
            </select>
          </div>
        </div>

        <button
          type="button"
          onClick={handleAddFolder}
          disabled={!newFolderName.trim()}
          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
        >
          + Add a document folder
        </button>

        <div className="border-t border-gray-200 dark:border-gray-700 pt-6 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Communications</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Create an EdgeMail folder under Case Files, route future messages, and backfill matching mail from this mailbox.
              </p>
            </div>
            <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                checked={emailAutomation.enabled}
                onChange={(event) => updateEmailAutomation({ enabled: event.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Enable
            </label>
          </div>

          {emailAutomation.enabled && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Mailbox
                  </label>
                  <select
                    value={emailAutomation.mailboxId}
                    onChange={(event) => updateEmailAutomation({ mailboxId: event.target.value })}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    {mailAccounts.length === 0 && <option value="">No mailbox available</option>}
                    {mailAccounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.email}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    EdgeMail folder
                  </label>
                  <div className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white">
                    Case Files / {formData.details.clientReferenceNumber || existingMatter?.matter_number || 'Engagement number'}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={emailAutomation.includeExisting}
                    onChange={(event) => updateEmailAutomation({ includeExisting: event.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Backfill existing mail
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={emailAutomation.createFutureRule}
                    onChange={(event) => updateEmailAutomation({ createFutureRule: event.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Route future mail
                </label>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h5 className="text-sm font-medium text-gray-800 dark:text-gray-200">Participants</h5>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Matches From, To, Cc, and Bcc
                  </span>
                </div>

                {emailAutomation.participants.length > 0 && (
                  <div className="space-y-2">
                    {emailAutomation.participants.map((participant) => (
                      <div key={participant.email} className="flex items-center justify-between rounded-md bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 px-3 py-2">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {participant.name || participant.email}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{participant.email}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => updateEmailAutomation({
                            participants: emailAutomation.participants.filter((item) => item.email !== participant.email),
                          })}
                          className="text-xs text-red-600 dark:text-red-400 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {relatedContactSuggestions.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {relatedContactSuggestions.map((contact) => (
                      <button
                        key={contact.id}
                        type="button"
                        onClick={() => addParticipant({ name: contact.name, email: contact.email! })}
                        className="px-3 py-1.5 rounded-full border border-blue-200 dark:border-blue-700 text-xs text-blue-700 dark:text-blue-200 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30"
                      >
                        + {contact.name}
                      </button>
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3">
                  <input
                    type="text"
                    value={newParticipantName}
                    onChange={(event) => setNewParticipantName(event.target.value)}
                    placeholder="Name"
                    className="p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                  <input
                    type="email"
                    value={newParticipantEmail}
                    onChange={(event) => setNewParticipantEmail(event.target.value)}
                    placeholder="person@domain.com"
                    className="p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                  <button
                    type="button"
                    onClick={handleAddParticipant}
                    disabled={!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newParticipantEmail.trim())}
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                  >
                    Add
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {emailAutomationStatus || (emailAutomationPreview?.targetFolderPath
                    ? `${emailAutomationPreview.outsideTargetCount} existing message(s) need filing into ${emailAutomationPreview.targetFolderPath}.`
                    : 'Preview before saving an existing engagement. New engagements will run after creation.')}
                </div>
                <button
                  type="button"
                  onClick={() => previewMatterEmailAutomation().catch((error) => {
                    console.error('[MatterCreateForm] Email automation preview failed:', error);
                    setEmailAutomationStatus('Preview failed. Save was not changed.');
                  })}
                  disabled={!matterId || !emailAutomation.mailboxId || emailAutomation.participants.length === 0}
                  className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  Preview
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Reports Section
  const renderReportsSection = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <p className="text-sm text-blue-800 dark:text-blue-200">
          Attorney allocation: Select the percentage of collected funds to allocate to originating 
          and/or responsible attorneys. Previously generated reports will not be affected by updated 
          attorney allocations.
          <InfoTooltip content="Attorney allocation determines how collected funds are split between originating and responsible attorneys." />
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Originating attorney allocation
          </label>
          <div className="flex items-center space-x-2">
            <input
              type="number"
              min="0"
              max="100"
              value={formData.reports.originatingAttorneyAllocation}
              onChange={(e) => updateSection('reports', { originatingAttorneyAllocation: Number(e.target.value) })}
              className="flex-1 p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            />
            <span className="text-gray-500 dark:text-gray-400">%</span>
          </div>
          <label className="flex items-center mt-2">
            <input
              type="checkbox"
              checked={formData.reports.useFirmSettingsOriginating}
              onChange={(e) => updateSection('reports', { useFirmSettingsOriginating: e.target.checked })}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Use firm settings</span>
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Responsible attorney allocation
          </label>
          <div className="flex items-center space-x-2">
            <input
              type="number"
              min="0"
              max="100"
              value={formData.reports.responsibleAttorneyAllocation}
              onChange={(e) => updateSection('reports', { responsibleAttorneyAllocation: Number(e.target.value) })}
              className="flex-1 p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            />
            <span className="text-gray-500 dark:text-gray-400">%</span>
          </div>
          <label className="flex items-center mt-2">
            <input
              type="checkbox"
              checked={formData.reports.useFirmSettingsResponsible}
              onChange={(e) => updateSection('reports', { useFirmSettingsResponsible: e.target.checked })}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Use firm settings</span>
          </label>
        </div>
      </div>
    </div>
  );

  // Render current section content
  const renderSectionContent = () => {
    switch (currentSection) {
      case 'template':
        return renderTemplateSection();
      case 'details':
        return renderDetailsSection();
      case 'relatedProceedings':
        return renderRelatedProceedingsSection();
      case 'permissions':
        return renderPermissionsSection();
      case 'notifications':
        return renderNotificationsSection();
      case 'blockUsers':
        return renderBlockUsersSection();
      case 'relatedContacts':
        return renderRelatedContactsSection();
      case 'customFields':
        return renderCustomFieldsSection();
      case 'billing':
        return renderBillingSection();
      case 'taskLists':
        return renderTaskListsSection();
      case 'documentFolders':
        return renderDocumentFoldersSection();
      case 'reports':
        return renderReportsSection();
      default:
        return <div>Section not found</div>;
    }
  };

  // Navigation between sections
  const currentSectionIndex = FORM_SECTIONS.findIndex(section => section.id === currentSection);
  const canGoNext = currentSectionIndex < FORM_SECTIONS.length - 1;
  const canGoPrevious = currentSectionIndex > 0;

  const goToNextSection = () => {
    if (canGoNext) {
      const nextSection = FORM_SECTIONS[currentSectionIndex + 1];
      navigateToSection(nextSection.id);
    }
  };

  const goToPreviousSection = () => {
    if (canGoPrevious) {
      const previousSection = FORM_SECTIONS[currentSectionIndex - 1];
      navigateToSection(previousSection.id);
    }
  };

  // Show loading screen during initialization
  if (!isInitialized || isLoadingMatter) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {isEditMode ? 'Loading Engagement for Editing' : 'Initializing New Engagement Form'}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Step {initializationStep} of {isEditMode ? '3' : '4'}: {
              initializationStep === 1 ? 'Loading contacts and engagements...' :
              initializationStep === 2 ? (isEditMode ? 'Loading existing engagement...' : 'Setting up templates...') :
              initializationStep === 3 ? (isEditMode ? 'Setting up templates...' : 'Configuring form...') :
              'Checking for saved drafts...'
            }
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex bg-gray-50 dark:bg-gray-900">
      {/* Sidebar Navigation */}
      <div className="w-80 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col h-screen">
        <div className="flex-shrink-0 p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <div className="w-5 h-5 mr-2 text-blue-600">
              <MattersIcon />
            </div>
            {isEditMode ? 'Edit Engagement' : 'New Engagement'}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {isEditMode ? 'Update engagement information' : 'Complete all sections to create your engagement'}
          </p>
          {isEditMode && existingMatter && (
            <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-md">
              <p className="text-xs text-blue-700 dark:text-blue-300">
                Editing: {existingMatter.displayNumber || existingMatter.matter_number}
              </p>
            </div>
          )}
          
          {/* Progress indicator */}
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
              <span>Progress</span>
              <span>{completedSections.size} / {FORM_SECTIONS.length}</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(completedSections.size / FORM_SECTIONS.length) * 100}%` }}
              />
            </div>
          </div>

          {/* 🆕 COMPACT REQUIRED FIELDS QUEUE */}
          <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-blue-900 dark:text-blue-200">
                📋 Required
              </span>
              <span className={`text-xs px-1.5 py-0.5 rounded ${
                isFormReadyToSubmit 
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' 
                  : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
              }`}>
                {completedRequired}/{totalRequired}
              </span>
            </div>
            
            <div className="flex items-center justify-between text-xs">
              {Object.entries(requiredFieldsStatus).map(([key, field]) => (
                <div 
                  key={key}
                  className="cursor-pointer hover:opacity-75"
                  onClick={() => setCurrentSection(field.section)}
                  title={field.label}
                >
                  <span className={`${
                    field.completed 
                      ? 'text-green-600 dark:text-green-400' 
                      : 'text-red-500 dark:text-red-400'
                  }`}>
                    {field.completed ? '✅' : '❌'}
                  </span>
                </div>
              ))}
            </div>

            {isFormReadyToSubmit ? (
              <div className="mt-1 text-xs text-green-700 dark:text-green-400 font-medium">
                🎉 Ready!
              </div>
            ) : (
              <div className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                Complete to proceed
              </div>
            )}
          </div>

          {/* Auto-save indicator - only in create mode */}
          {!isEditMode && isAutoSaving && (
            <div className="mt-3 flex items-center text-sm text-blue-600 dark:text-blue-400">
              <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full mr-2" />
              Auto-saving...
            </div>
          )}
        </div>

        {/* Scrollable Navigation */}
        <div className="flex-1 overflow-y-auto p-4 min-h-0">
          <nav className="space-y-2">
            {FORM_SECTIONS.map((section) => {
              const IconComponent = section.icon;
              const isActive = currentSection === section.id;
              const isCompleted = completedSections.has(section.id);
              const hasErrors = validationErrors[section.id]?.length > 0;

              return (
                <button
                  key={section.id}
                  onClick={() => navigateToSection(section.id)}
                  className={`w-full flex items-center px-3 py-2 text-left rounded-lg transition-colors ${
                    isActive
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className={`w-5 h-5 mr-3 ${isActive ? section.color : 'text-gray-400'}`}>
                    <IconComponent />
                  </div>
                  <span className="flex-1">{section.name}</span>
                  
                  {/* Status indicators */}
                  <div className="flex items-center space-x-1">
                    {hasErrors && (
                      <div className="w-2 h-2 bg-red-500 rounded-full" title="Has errors" />
                    )}
                    {isCompleted && !hasErrors && (
                      <div className="w-2 h-2 bg-green-500 rounded-full" title="Completed" />
                    )}
                  </div>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col h-screen">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {FORM_SECTIONS.find(s => s.id === currentSection)?.name || 'New Engagement'}
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Section {currentSectionIndex + 1} of {FORM_SECTIONS.length}
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={() => {
                  if (!isEditMode) localStorage.removeItem('webelieve-engagement-draft');
                  navigate(isEditMode ? `/engagements/${matterId}` : '/engagements');
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!isEditMode && !isFormReadyToSubmit}
                className={`px-6 py-2 rounded-lg transition-colors font-medium ${
                  !isEditMode && !isFormReadyToSubmit
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
                title={!isEditMode && !isFormReadyToSubmit ? 'Complete required fields to create engagement' : ''}
              >
                {isEditMode 
                  ? 'Save Updates' 
                  : isFormReadyToSubmit 
                    ? '✅ Create Engagement'
                    : `❌ Create Engagement (${completedRequired}/${totalRequired})`
                }
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 pb-16 min-h-0">
          <div className="max-w-4xl mx-auto">
            {renderSectionContent()}
          </div>
        </div>

        {/* Footer Navigation */}
        <div className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex-shrink-0 shadow-lg min-h-[80px]">
          <div className="flex items-center justify-between max-w-4xl mx-auto h-full">
            <button
              onClick={goToPreviousSection}
              disabled={!canGoPrevious}
              className="px-6 py-3 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              ← Previous
            </button>
            
            <div className="text-sm text-gray-500 dark:text-gray-400 font-medium">
              Step {currentSectionIndex + 1} of {FORM_SECTIONS.length}
            </div>
            
            {canGoNext ? (
              <button
                onClick={goToNextSection}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Next →
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!isEditMode && !isFormReadyToSubmit}
                className={`px-8 py-3 rounded-lg transition-colors font-medium shadow-md ${
                  !isEditMode && !isFormReadyToSubmit
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
                title={!isEditMode && !isFormReadyToSubmit ? 'Complete required fields to create engagement' : ''}
              >
                {isEditMode 
                  ? 'Save Updates' 
                  : isFormReadyToSubmit 
                    ? '🎉 Create Engagement'
                    : `Complete Required Fields (${completedRequired}/${totalRequired})`
                }
              </button>
            )}
          </div>
        </div>
      </div>

      {/* EdgeLex Notice Modal */}
      {noticeModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] px-4">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl max-w-md w-full">
            <div className="flex items-start gap-3 p-5 border-b border-gray-200 dark:border-gray-700">
              <div className={`mt-1 h-3 w-3 rounded-full ${
                noticeModal.tone === 'error'
                  ? 'bg-red-500'
                  : noticeModal.tone === 'success'
                    ? 'bg-green-500'
                    : 'bg-blue-500'
              }`} />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {noticeModal.title}
                </h3>
                <p className="mt-2 whitespace-pre-line text-sm leading-6 text-gray-600 dark:text-gray-300">
                  {noticeModal.message}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setNoticeModal(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                aria-label="Close notice"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex justify-end p-4">
              <button
                type="button"
                onClick={() => setNoticeModal(null)}
                className={`px-5 py-2 rounded-lg font-medium text-white transition-colors ${
                  noticeModal.tone === 'error'
                    ? 'bg-red-600 hover:bg-red-700'
                    : noticeModal.tone === 'success'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Confirmation Modal */}
      {showChangeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Confirm Matter Changes
              </h3>
              <button
                onClick={() => {
                  setShowChangeModal(false);
                  setPendingUpdates(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {changesSummary.length > 0 ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Please review the following changes before saving:
                </p>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {changesSummary.map((change, index) => (
                    <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-md p-3">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {change.field}
                      </div>
                      <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                        <div className="flex items-center space-x-2">
                          <span className="text-red-600 dark:text-red-400">From:</span>
                          <span className="font-mono bg-red-50 dark:bg-red-900/20 px-1 rounded">
                            {change.oldValue || '(empty)'}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="text-green-600 dark:text-green-400">To:</span>
                          <span className="font-mono bg-green-50 dark:bg-green-900/20 px-1 rounded">
                            {change.newValue || '(empty)'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                    Are these changes correct?
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  No changes were detected in the matter data.
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Do you want to continue anyway?
                </p>
              </div>
            )}

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowChangeModal(false);
                  setPendingUpdates(null);
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                No, Go Back
              </button>
              <button
                onClick={confirmAndSaveChanges}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Yes, Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Preview Side Panel */}
      {previewPanelTemplateId && (
        <TaskTemplatePreviewPanel
          templateId={previewPanelTemplateId}
          jurisdictionCode={formData.details.jurisdictionCode}
          triggerDate={new Date().toISOString().slice(0, 10)}
          onClose={() => setPreviewPanelTemplateId(null)}
        />
      )}

      {/* Template Comparison Modal (Sprint 4I) */}
      {showComparisonModal && (
        <TaskTemplateComparisonModal
          templates={filteredAndRankedTaskLists.filter(tl => !formData.taskLists.taskListIds.includes(tl.id)).slice(0, 3)}
          onClose={() => setShowComparisonModal(false)}
          onSelect={(templateId) => {
            if (!formData.taskLists.taskListIds.includes(templateId)) {
              updateSection('taskLists', {
                taskListIds: [...formData.taskLists.taskListIds, templateId]
              });
            }
          }}
        />
      )}
    </div>
  );
};

export default MatterCreateForm;
